<template>
  <UCard v-if="workflow" class="border-l-4 p-1" :class="activeProgressSteps.find(step => step.id === mapStatusForDisplay(workflow.status))?.borderClass" variant="subtle" :ui="{ body: 'p-1 sm:p-1' }">
    <div class="w-full flex-col justify-center items-center p-1 mb-3">
      <ol class="flex md:flex-row flex-col md:items-start items-center justify-between w-full md:gap-1 gap-2 relative">
        <li v-if="showRejectionMarker" class="group flex relative justify-start z-10 md:ml-[5.3%]">
          <div class="mr-1 z-10 flex flex-col items-center justify-start gap-2">
            <UTooltip v-if="rejectionEventLog" :text="`${rejectionEventLog?.createdBy?.fullName} · ${formatDate(rejectionEventLog?.createdDate, 'datetime', locale)}`">
              <div class="relative">
                <div class="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all duration-300 bg-red-500 border-2 border-red-500">
                  <UIcon :name="rejectionMarkerStep?.icon" class="text-sm h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
              </div>
            </UTooltip>
            <div v-else class="relative">
              <div class="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all duration-300 bg-red-500 border-2 border-red-500">
                <UIcon :name="rejectionMarkerStep?.icon" class="text-sm h-4 w-4 sm:h-5 sm:w-5 text-white" />
              </div>
            </div>
            
            <div class="justify-center items-center gap-1 inline-flex flex-col max-w-[120px] sm:max-w-[155px]">
              <h5 class="text-center text-red-600 dark:text-red-400 text-xs sm:text-sm font-semibold leading-tight break-words">{{ rejectionMarkerStep?.label }}</h5>
              
              <div v-if="currentProcessStepLabel" class="mt-0.5">
                <div class="inline-flex items-center px-2 py-0.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-full">
                  <span class="text-xs font-medium text-red-700 dark:text-red-300">{{ currentProcessStepLabel }}</span>
                </div>
              </div>
            </div>
          </div>
        </li>
        
        <li v-else v-for="(status, index) in filteredSteps" :key="status.id" class="group flex relative justify-start z-10 flex-1" :class="{ 'flex-1': index !== filteredSteps.length - 1 }">
          <div v-if="index !== filteredSteps.length - 1" class="absolute hidden md:flex items-center w-full h-[3px] z-0" :style="{ top: '18.5px', left: 'calc(50% + 19px)', right: 'calc(-50% + 19px)' }">
            <div class="w-full h-[3px] bg-gray-200 dark:bg-gray-700 relative">
              <div class="h-full transition-all duration-500 relative" :class="getProgressFillClass(status.id, filteredSteps[index + 1]?.id)" :style="getProgressFillStyle(status.id, filteredSteps[index + 1]?.id)"></div>
            </div>
          </div>

          <div class="w-full z-10 flex flex-col items-center justify-start gap-2">
            <UTooltip v-if="getEventLogByStep(status.id)" :text="`${getEventLogByStep(status.id)?.createdBy?.fullName} · ${formatDate(getEventLogByStep(status.id)?.createdDate, 'datetime', locale)}`">
              <div class="relative cursor-pointer">
                <div class="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all duration-300" :class="getStepCircleClass(status.id)">
                  <UIcon :name="status.icon" class="text-sm h-4 w-4 sm:h-5 sm:w-5 text-white dark:text-gray-900" />
                </div>
                <div v-if="isCurrentStep(status.id) && !isLastStep(status.id)" class="absolute inset-0 rounded-full animate-ping bg-blue-500 opacity-50"></div>
              </div>
            </UTooltip>
            <div v-else class="relative">
              <div class="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all duration-300" :class="getStepCircleClass(status.id)">
                <UIcon :name="status.icon" class="text-sm h-4 w-4 sm:h-5 sm:w-5 text-white dark:text-gray-900" />
              </div>
              <div v-if="isCurrentStep(status.id) && !isLastStep(status.id)" class="absolute inset-0 rounded-full animate-ping bg-blue-500 opacity-50"></div>
            </div>

            <div class="justify-center items-center gap-1 inline-flex flex-col max-w-[120px] sm:max-w-[155px]">
              <h5 class="text-center text-xs sm:text-sm leading-tight break-words transition-colors duration-300" :class="getStepTextClass(status.id)">{{ status.label }}</h5>
              
              <div v-if="isCurrentStep(status.id) && currentProcessStepLabel && (isQuotationPhase || isVehicleOrderType(workflow.purchaseOrders?.[purchaseOrderIndex]?.type) || workflow.purchaseOrders?.[purchaseOrderIndex]?.currentProcessStep === TyreOrderStep.Parked)" class="mt-0.5">
                <div class="inline-flex items-center px-2 py-0.5 rounded-full border" :class="isLastStep(status.id) ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'">
                  <span class="text-xs text-center font-medium" :class="isLastStep(status.id) ? 'text-green-700 dark:text-green-300' : 'text-blue-700 dark:text-blue-300'">{{ currentProcessStepLabel }}</span>
                </div>
              </div>
            </div>
          </div>
        </li>
      </ol>
      
      <div v-if="isQuotationPhase && workflow?.tenant === Tenant.FI" class="mt-3 flex justify-start">
        <div class="inline-block px-2 py-1.5 bg-(--ui-bg-muted) rounded-lg border border-gray-200 dark:border-gray-700 ml-2 md:ml-14 max-w-full overflow-x-auto">
          <!--<div class="absolute -top-2 md:left-[72px] w-4 h-4 bg-(--ui-bg-muted) border-t border-l border-gray-200 dark:border-gray-700 transform rotate-45"></div>-->
          <ApprovalFlowVisualization :steps="workflow?.quotation?.clientApprovalConfiguration" :current-step-index="currentClientApprovalStepIndex" :status="workflow?.status" />
          
          <div v-if="[QuotationStep.NotRegisteredUserApproval].includes(workflow.quotation?.currentProcessStep) && !slug && [Permission.CustomerCare].some(p => userStore.hasPermission(p))" class="mt-2 flex justify-center pt-2">
            <UButton
              color="neutral"
              variant="outline"
              icon="i-heroicons-envelope"
              size="xs"
              class="dark:text-white cursor-pointer"
              :loading="notRegisteredUserResendLoading"
              :disabled="notRegisteredUserResendLoading"
              @click="resendNotRegisteredUserApprovalLink"
            >
              {{ t('processStatus.resendNotRegisteredUserApprovalLink') }}
            </UButton>
          </div>
        </div>
      </div>
    </div>
    
    <div v-if="showClientFilledDriver" class="mb-3 mx-2 md:mx-14">
      <div class="inline-flex flex-col gap-1 px-3 py-2 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
        <div class="flex items-center gap-1.5">
          <UIcon name="i-lucide-user-check" class="w-4 h-4 text-green-600 dark:text-green-400" />
          <span class="text-xs font-semibold text-green-700 dark:text-green-300">{{ t('processStatus.driverFilledByClient') }}</span>
        </div>
        <div class="pl-5.5 text-sm text-default">
          <div class="font-medium">{{ clientFilledDriverContact.firstName }} {{ clientFilledDriverContact.surname }}</div>
          <div v-if="clientFilledDriverContact.phoneNumber" class="text-xs text-default">{{ clientFilledDriverContact.phoneNumber }}</div>
          <div v-if="clientFilledDriverContact.email" class="text-xs text-default">{{ clientFilledDriverContact.email }}</div>
        </div>
        <div v-if="workflow?.replacedCar?.licensePlate" class="mt-1.5">
          <div class="flex items-center gap-1.5">
            <UIcon name="i-mdi-car-hatchback" class="w-4 h-4 text-green-600 dark:text-green-400" />
            <span class="text-xs font-semibold text-green-700 dark:text-green-300">{{ t('processStatus.replacedCarRegNumber') }}</span>
          </div>
          <div class="pl-5.5 text-sm font-medium text-highlighted">{{ workflow.replacedCar.licensePlate }}</div>
        </div>
      </div>
    </div>
    
    <WorkflowActionCard
      v-if="[QuotationStep.SchemaFinalization].includes(workflow.quotation?.currentProcessStep) && [Permission.CustomerCare].some(p => userStore.hasPermission(p)) && (isUserAssignedToQuotationStep || isRoleAssignedToQuotationStep)"
      :card-message="t('processStatus.actionRequiredQuotationSchemaFinalization')"
      :show-approve-button="true"
      :show-reject-button="true"
      :action-loading="actionLoading"
      @approve="approveStep(QuotationStep.SchemaFinalization)"
      @reject="rejectStep(QuotationStep.SchemaFinalization)"
    >
      <template #inputs>
        <div class="pb-2 flex justify-center">
          <UButton
            color="neutral"
            variant="outline"
            icon="i-heroicons-cog-6-tooth"
            class="h-8 text-sm dark:text-white cursor-pointer"
            @click="isWorkflowApprovalModalOpen = true"
          >
            {{ t('processStatus.manageApprovalFlow') }}
          </UButton>
        </div>
      </template>
    </WorkflowActionCard>
    
    <WorkflowActionCard v-if="[QuotationStep.Parked].includes(workflow.quotation?.currentProcessStep) && [Permission.CustomerCare, Permission.PurchaseSupport, Permission.ClientAccount, Permission.Backoffice, Permission.KeyAccountManager].some(p => userStore.hasPermission(p)) && (isUserAssignedToQuotationStep || isRoleAssignedToQuotationStep || isUserAssignedToStep || isRoleAssignedToStep)" :card-message="(workflow.purchaseOrders?.length ?? 0) > 0 ? t('processStatus.actionRequiredQuotationParkedWithOrders') : t('processStatus.actionRequiredQuotationParked')" :action-loading="actionLoading">
      <template #inputs>
        <div class="flex flex-wrap justify-center gap-2 pb-2">
          <UButton
            color="primary"
            icon="i-heroicons-arrow-uturn-left"
            class="h-8 text-sm dark:text-white cursor-pointer"
            :disabled="actionLoading"
            :loading="actionLoading"
            @click="unparkWorkflow(UnparkScenario.RestartFromQuotation)"
          >
            {{ t('processStatus.unpark.restartFromQuotation') }}
          </UButton>
          <UButton
            v-if="(workflow.purchaseOrders?.length ?? 0) > 0"
            color="primary"
            variant="subtle"
            icon="i-heroicons-arrow-uturn-left"
            class="h-8 text-sm dark:text-white cursor-pointer"
            :disabled="actionLoading"
            :loading="actionLoading"
            @click="unparkWorkflow(UnparkScenario.RestartFromPurchaseOrder)"
          >
            {{ t('processStatus.unpark.restartFromPurchaseOrder') }}
          </UButton>
          <UButton
            color="neutral"
            variant="subtle"
            icon="i-heroicons-play"
            class="h-8 text-sm dark:text-white cursor-pointer"
            :disabled="actionLoading"
            :loading="actionLoading"
            @click="unparkWorkflow(UnparkScenario.NoRestart)"
          >
            {{ t('processStatus.unpark.noRestart') }}
          </UButton>
        </div>
      </template>
    </WorkflowActionCard>
    
    <WorkflowActionCard v-if="workflow.purchaseOrders?.[purchaseOrderIndex]?.currentProcessStep === VehicleOrderStep.Parked && [QuotationStep.OrderCreation, QuotationStep.Completed].includes(workflow.quotation?.currentProcessStep) && [Permission.CustomerCare, Permission.ClientAccount, Permission.Backoffice, Permission.KeyAccountManager].some(p => userStore.hasPermission(p))" :card-message="t('processStatus.actionRequiredOrderParked')" :action-loading="actionLoading">
      <template #inputs>
        <div class="flex flex-wrap justify-center gap-2 pb-2">
          <UButton
            color="primary"
            icon="i-heroicons-arrow-path"
            class="h-8 text-sm dark:text-white cursor-pointer"
            :disabled="actionLoading"
            :loading="actionLoading"
            @click="unparkOrder(UnparkOrderScenario.Restart)"
          >
            {{ t('processStatus.orderUnpark.restart') }}
          </UButton>
          <UButton
            color="neutral"
            variant="subtle"
            icon="i-heroicons-play"
            class="h-8 text-sm dark:text-white cursor-pointer"
            :disabled="actionLoading"
            :loading="actionLoading"
            @click="unparkOrder(UnparkOrderScenario.NoRestart)"
          >
            {{ t('processStatus.orderUnpark.noRestart') }}
          </UButton>
        </div>
      </template>
    </WorkflowActionCard>
    
    <WorkflowActionCard
      v-if="[QuotationStep.ClientApproval].includes(workflow.quotation?.currentProcessStep) && [Permission.FleetManager, Permission.ClientAccount, Permission.Backoffice, Permission.KeyAccountManager].some(p => userStore.hasPermission(p)) && (isUserAssignedToQuotationStep || isRoleAssignedToQuotationStep)"
      :card-message="t('processStatus.actionRequiredQuotationClientApproval')"
      :show-approve-button="true"
      :show-reject-button="true"
      :approve-label="t('processStatus.actions.approveAndConfirm')"
      :approve-disabled="isSubmitQuotationAdjustmentDisabled"
      :action-loading="actionLoading"
      :require-reject-reason="true"
      @approve="submitQuotationAdjustments(QuotationStep.ClientApproval)"
      @reject="rejectStep(QuotationStep.ClientApproval, $event?.reason)"
    >
      <template #inputs>
        <!--
        <div class="flex flex-wrap gap-2 space-y-5">
          <div class="relative mb-5 w-full">
            <div class="flex items-center gap-1.5">
              <USelect
                v-model="selectedDriverOption"
                :items="driverSelectOptions"
                color="neutral"
                highlight
                :placeholder="t('processStatus.selectDriver')"
                class="w-[34rem]"
              />
              <button
                v-if="selectedDriverOption"
                @click="selectedDriverOption = undefined"
                class="p-1 rounded-full bg-white dark:bg-gray-800 ring-1 ring-red-400 dark:ring-red-500 transition-colors hover:bg-red-50 dark:hover:bg-red-900/50 shadow-sm"
                type="button"
              >
                <UIcon name="i-heroicons-x-mark" class="w-3.5 h-3.5 text-red-500 dark:text-red-400" />
              </button>
            </div>
            <label class="pointer-events-none absolute left-0 -top-2.5 text-highlighted text-xs font-medium px-1.5">
              <span class="inline-flex bg-default px-1 rounded">{{ t('processStatus.selectDriver') }}</span>
            </label>
          </div>
        </div>
        -->
        <div class="flex flex-col items-center gap-3 mb-6">
          <div class="inline-flex rounded-lg border border-default bg-default p-0.5">
            <button
              type="button"
              @click="wantsNewDriver = false"
              :class="[
                'flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium transition-colors',
                !wantsNewDriver ? 'bg-primary text-inverted' : 'text-dimmed hover:text-default'
              ]"
            >
              <UIcon name="i-lucide-user-x" class="w-4 h-4" />
              {{ t('processStatus.dontKnowDriver') }}
            </button>
            <button
              type="button"
              @click="wantsNewDriver = true"
              :class="[
                'flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium transition-colors',
                wantsNewDriver ? 'bg-primary text-inverted' : 'text-dimmed hover:text-default'
              ]"
            >
              <UIcon name="i-lucide-user-plus" class="w-4 h-4" />
              {{ t('processStatus.knowDriver') }}
            </button>
          </div>
          <div v-if="wantsNewDriver" class="flex flex-wrap justify-center gap-2 gap-y-5 mt-3">
            <div class="relative">
              <UInput
                v-model="driverFirstName"
                color="neutral"
                highlight
                placeholder=""
                class="w-38 h-7 text-sm"
                :ui="{ base: 'peer' }"
              >
                <label class="pointer-events-none absolute left-0 -top-2.5 text-highlighted text-xs font-medium px-1.5 transition-all peer-focus:-top-2.5 peer-focus:text-highlighted peer-focus:text-xs peer-focus:font-medium peer-placeholder-shown:text-sm peer-placeholder-shown:text-dimmed peer-placeholder-shown:top-1.5 peer-placeholder-shown:font-medium">
                  <span class="inline-flex bg-default px-1 rounded">{{ t('processStatus.driverFirstName') }}</span>
                </label>
              </UInput>
            </div>
            <div class="relative">
              <UInput
                v-model="driverLastName"
                color="neutral"
                highlight
                placeholder=""
                class="w-38 h-7 text-sm"
                :ui="{ base: 'peer' }"
              >
                <label class="pointer-events-none absolute left-0 -top-2.5 text-highlighted text-xs font-medium px-1.5 transition-all peer-focus:-top-2.5 peer-focus:text-highlighted peer-focus:text-xs peer-focus:font-medium peer-placeholder-shown:text-sm peer-placeholder-shown:text-dimmed peer-placeholder-shown:top-1.5 peer-placeholder-shown:font-medium">
                  <span class="inline-flex bg-default px-1 rounded">{{ t('processStatus.driverLastName') }}</span>
                </label>
              </UInput>
            </div>
            <PhoneInput
              v-model="driverPhone"
              :label="t('processStatus.driverPhone')"
            />
            <div class="relative">
              <UInput
                v-model="driverEmail"
                type="email"
                color="neutral"
                highlight
                placeholder=""
                class="w-42 h-7 text-sm"
                :ui="{ base: 'peer' }"
              >
                <label class="pointer-events-none absolute left-0 -top-2.5 text-highlighted text-xs font-medium px-1.5 transition-all peer-focus:-top-2.5 peer-focus:text-highlighted peer-focus:text-xs peer-focus:font-medium peer-placeholder-shown:text-sm peer-placeholder-shown:text-dimmed peer-placeholder-shown:top-1.5 peer-placeholder-shown:font-medium">
                  <span class="inline-flex bg-default px-1 rounded">{{ t('processStatus.driverEmail') }}</span>
                </label>
              </UInput>
            </div>
          </div>
        </div>
        <div class="flex flex-wrap gap-2 space-y-2">
          <div class="relative">
            <UInput
              v-model="replacedPlate"
              color="neutral"
              highlight
              placeholder=""
              class="w-48 h-7"
              :maxlength="10"
              :ui="{ base: 'peer font-mono text-sm tracking-[0.12em] uppercase' }"
            >
              <label class="pointer-events-none absolute left-0 -top-2.5 text-highlighted text-xs font-medium px-1.5 transition-all peer-focus:-top-2.5 peer-focus:text-highlighted peer-focus:text-xs peer-focus:font-medium peer-placeholder-shown:text-sm peer-placeholder-shown:text-dimmed peer-placeholder-shown:top-1.5 peer-placeholder-shown:font-medium">
                <span class="inline-flex bg-default px-1 rounded">{{ t('processStatus.replacedCarRegNumber') }}</span>
              </label>
            </UInput>
          </div>
          <div class="relative">
            <USelect
              v-model="meaningRequiredDeliveryDate"
              :items="[
                { label: t('workflowDetail.meaningDeliveryDate.asap'), value: 'ASAP' },
                { label: t('workflowDetail.meaningDeliveryDate.onDate'), value: 'EQ' },
                { label: t('workflowDetail.meaningDeliveryDate.afterDate'), value: 'GE' },
                { label: t('workflowDetail.meaningDeliveryDate.beforeDate'), value: 'LE' }
              ]"
              color="neutral"
              highlight
              :placeholder="t('workflowDetail.common.select')"
              class="w-50"
            >
            </USelect>
            <label class="pointer-events-none absolute left-0 -top-2.5 text-highlighted text-xs font-medium px-1.5 transition-all peer-focus:-top-2.5 peer-focus:text-highlighted peer-focus:text-xs peer-focus:font-medium peer-placeholder-shown:text-sm peer-placeholder-shown:text-dimmed peer-placeholder-shown:top-1.5 peer-placeholder-shown:font-medium">
              <span class="inline-flex bg-default px-1 rounded">{{ t('processStatus.meaningOfReqDeliveryDate') }}</span>
            </label>
          </div>
          <div class="relative">
            <div v-if="['LE', 'GE', 'EQ'].includes(meaningRequiredDeliveryDate)">
              <UPopover v-model:open="isRequiredDeliveryDatePopoverOpen">
                <UButton
                  color="neutral"
                  variant="outline"
                  class="w-44 text-sm justify-start cursor-pointer text-left focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-inverted ring ring-inset ring-inverted"
                  :class="requiredDeliveryDate ? '' : 'text-dimmed'"
                >
                  <UIcon name="i-lucide-calendar" class="w-4 h-4 mr-0.5"/>
                  {{ requiredDeliveryDate ? formatDate(requiredDeliveryDate, 'short', locale) : t('workflowDetail.common.select')  }}
                </UButton>
                <template #content>
                  <UCalendar v-model="requiredDeliveryDate" :locale="locale" :min-value="requiredDeliveryDateMinDate" :is-date-disabled="requiredDeliveryDateDisabledDates" :year-controls="false" @update:model-value="isRequiredDeliveryDatePopoverOpen = false" />
                </template>
              </UPopover>
              <label class="pointer-events-none absolute left-0 -top-2.5 text-highlighted text-xs font-medium px-1.5 transition-all peer-focus:-top-2.5 peer-focus:text-highlighted peer-focus:text-xs peer-focus:font-medium peer-placeholder-shown:text-sm peer-placeholder-shown:text-dimmed peer-placeholder-shown:top-1.5 peer-placeholder-shown:font-medium">
                <span class="inline-flex bg-default px-1 rounded">{{ t('processStatus.requiredDeliveryDate') }}</span>
              </label>
            </div>
          </div>
        </div>
      </template>
    </WorkflowActionCard>
    
    <WorkflowActionCard
      v-if="[QuotationStep.DriverApproval].includes(workflow.quotation?.currentProcessStep) && [Permission.Driver].some(p => userStore.hasPermission(p)) && (isUserAssignedToQuotationStep || isRoleAssignedToQuotationStep)"
      :card-message="t('processStatus.actionRequiredQuotationDriverApproval')"
      :show-approve-button="true"
      :show-reject-button="true"
      :approve-disabled="isSsnFlow && (driverLoading || socialSecurityNumber.trim() === '')"
      :action-loading="actionLoading"
      :require-reject-reason="true"
      @approve="approveStep(QuotationStep.DriverApproval)"
      @reject="rejectStep(QuotationStep.DriverApproval, $event?.reason)"
    >
      <template #inputs>
        <div v-if="isSsnFlow" class="pb-2">
          <div class="relative">
            <UInput
              v-model="socialSecurityNumber"
              color="neutral"
              highlight
              placeholder=""
              :maxlength="11"
              class="w-50 h-7 text-xs"
              :ui="{ base: 'peer uppercase' }"
            >
              <label class="pointer-events-none absolute left-0 -top-2.5 text-highlighted text-xs font-medium px-1.5 transition-all peer-focus:-top-2.5 peer-focus:text-highlighted peer-focus:text-xs peer-focus:font-medium peer-placeholder-shown:text-sm peer-placeholder-shown:text-dimmed peer-placeholder-shown:top-1.5 peer-placeholder-shown:font-medium">
                <span class="inline-flex bg-default px-1 rounded">{{ t('processStatus.socialSecurityNumber') }}</span>
              </label>
            </UInput>
          </div>
        </div>
      </template>
    </WorkflowActionCard>
    
    <WorkflowActionCard
      v-if="[QuotationStep.PurchaseSupportApproval].includes(workflow.quotation?.currentProcessStep) && [Permission.PurchaseSupport].some(p => userStore.hasPermission(p)) && (isUserAssignedToQuotationStep || isRoleAssignedToQuotationStep)"
      :card-message="t('processStatus.actionRequiredQuotationPurchaseSupportApproval')"
      :show-approve-button="true"
      :show-reject-button="true"
      :action-loading="actionLoading"
      @approve="approveStep(QuotationStep.PurchaseSupportApproval)"
      @reject="rejectStep(QuotationStep.PurchaseSupportApproval)"
    />
    
    <WorkflowActionCard
      v-if="[QuotationStep.HRApproval].includes(workflow.quotation?.currentProcessStep) && [Permission.HR].some(p => userStore.hasPermission(p)) && (isUserAssignedToQuotationStep || isRoleAssignedToQuotationStep)"
      :card-message="t('processStatus.actionRequiredQuotationHRApproval')"
      :show-approve-button="true"
      :show-reject-button="true"
      :action-loading="actionLoading"
      :require-reject-reason="true"
      @approve="approveStep(QuotationStep.HRApproval)"
      @reject="rejectStep(QuotationStep.HRApproval, $event?.reason)"
    />
    
    <WorkflowActionCard
      v-if="[QuotationStep.NotRegisteredUserApproval].includes(workflow.quotation?.currentProcessStep) && slug"
      :card-message="t('processStatus.actionRequiredQuotationNotRegisteredUserApproval')"
      :show-approve-button="true"
      :show-reject-button="true"
      :action-loading="actionLoading"
      :require-reject-reason="true"
      @approve="approveStep(QuotationStep.NotRegisteredUserApproval)"
      @reject="rejectStep(QuotationStep.NotRegisteredUserApproval, $event?.reason)"
    />
    
    <WorkflowActionCard
      v-if="[QuotationStep.FleetManagerApproval].includes(workflow.quotation?.currentProcessStep) && [Permission.FleetManager].some(p => userStore.hasPermission(p)) && (isUserAssignedToQuotationStep || isRoleAssignedToQuotationStep)"
      :card-message="t('processStatus.actionRequiredQuotationFleetManagerApproval')"
      :show-approve-button="true"
      :show-reject-button="true"
      :action-loading="actionLoading"
      :require-reject-reason="true"
      @approve="approveStep(QuotationStep.FleetManagerApproval)"
      @reject="rejectStep(QuotationStep.FleetManagerApproval, $event?.reason)"
    />
    
    <WorkflowActionCard
      v-if="[QuotationStep.SupervisorApproval].includes(workflow.quotation?.currentProcessStep) && (isUserAssignedToQuotationStep || isRoleAssignedToQuotationStep)"
      :card-message="t('processStatus.actionRequiredQuotationSupervisorApproval')"
      :show-approve-button="true"
      :show-reject-button="true"
      :action-loading="actionLoading"
      :require-reject-reason="true"
      @approve="approveStep(QuotationStep.SupervisorApproval)"
      @reject="rejectStep(QuotationStep.SupervisorApproval, $event?.reason)"
    />
    
    <WorkflowActionCard
      v-if="[QuotationStep.GroupApproval].includes(workflow.quotation?.currentProcessStep) && (isUserAssignedToQuotationStep || isRoleAssignedToQuotationStep)"
      :card-message="t('processStatus.actionRequiredQuotationGroupApproval')"
      :show-approve-button="true"
      :show-reject-button="true"
      :action-loading="actionLoading"
      :require-reject-reason="true"
      @approve="approveStep(QuotationStep.GroupApproval)"
      @reject="rejectStep(QuotationStep.GroupApproval, $event?.reason)"
    />
    
    <WorkflowActionCard
      v-if="[QuotationStep.PersonApproval].includes(workflow.quotation?.currentProcessStep) && (isUserAssignedToQuotationStep || isRoleAssignedToQuotationStep)"
      :card-message="t('processStatus.actionRequiredQuotationPersonApproval')"
      :show-approve-button="true"
      :show-reject-button="true"
      :action-loading="actionLoading"
      :require-reject-reason="true"
      @approve="approveStep(QuotationStep.PersonApproval)"
      @reject="rejectStep(QuotationStep.PersonApproval, $event?.reason)"
    />
    
    <WorkflowActionCard
      v-if="[QuotationStep.Rejection].includes(workflow.quotation?.currentProcessStep) && [Permission.CustomerCare, Permission.PurchaseSupport, Permission.ClientAccount, Permission.KeyAccountManager, Permission.Backoffice].some(p => userStore.hasPermission(p)) && (isUserAssignedToQuotationStep || isRoleAssignedToQuotationStep || isUserAssignedToStep || isRoleAssignedToStep)"
      :card-message="t('processStatus.actionRequiredQuotationRejection')"
      :show-approve-button="true"
      :action-loading="actionLoading"
      @approve="approveStep(QuotationStep.Rejection)"
    />
    
    <WorkflowActionCard
      v-if="(isCzechiaQuotation(workflow.quotation) || isNolsQuotation(workflow.quotation)) && [QuotationStep.ClientAccountAcceptation].includes(workflow.quotation?.currentProcessStep) && [Permission.ClientAccount, Permission.Backoffice, Permission.KeyAccountManager].some(p => userStore.hasPermission(p)) && (isUserAssignedToQuotationStep || isRoleAssignedToQuotationStep)"
      :card-message="t('processStatus.actionRequiredQuotationClientAccountAcceptation')"
      :show-approve-button="true"
      :show-reject-button="true"
      :action-loading="actionLoading"
      :require-reject-reason="workflow?.tenant === Tenant.CZ"
      @approve="approveStep(QuotationStep.ClientAccountAcceptation)"
      @reject="rejectStep(QuotationStep.ClientAccountAcceptation, $event?.reason)"
    >
      <template #inputs>
        <div class="flex flex-wrap gap-2 space-y-2 pb-2">
          <div class="relative">
            <UInput
              v-model="initialQuotationNumber"
              color="neutral"
              highlight
              placeholder=""
              class="w-50 h-7 text-xs"
              :ui="{ base: 'peer uppercase' }"
            >
              <label class="pointer-events-none absolute left-0 -top-2.5 text-highlighted text-xs font-medium px-1.5 transition-all peer-focus:-top-2.5 peer-focus:text-highlighted peer-focus:text-xs peer-focus:font-medium peer-placeholder-shown:text-sm peer-placeholder-shown:text-dimmed peer-placeholder-shown:top-1.5 peer-placeholder-shown:font-medium">
                <span class="inline-flex bg-default px-1 rounded">{{ t('processStatus.initialQuotationNumber') }}</span>
              </label>
            </UInput>
          </div>
          <div class="relative">
            <UInput
              v-model="frameworkPurchaseAgreement"
              color="neutral"
              highlight
              placeholder=""
              class="w-44 h-7 text-xs"
              :ui="{ base: 'peer' }"
            >
              <label class="pointer-events-none absolute left-0 -top-2.5 text-highlighted text-xs font-medium px-1.5 transition-all peer-focus:-top-2.5 peer-focus:text-highlighted peer-focus:text-xs peer-focus:font-medium peer-placeholder-shown:text-sm peer-placeholder-shown:text-dimmed peer-placeholder-shown:top-1.5 peer-placeholder-shown:font-medium">
                <span class="inline-flex bg-default px-1 rounded">RKS</span>
              </label>
            </UInput>
          </div>
        </div>
      </template>
    </WorkflowActionCard>
    
    <WorkflowActionCard
      v-if="[VehicleOrderStep.KamApproval].includes(workflow.purchaseOrders?.[purchaseOrderIndex]?.currentProcessStep) && [Permission.KeyAccountManager].some(p => userStore.hasPermission(p)) && (isUserAssignedToStep || isRoleAssignedToStep)"
      :card-message="t('processStatus.actionRequiredVehicleOrderStepKamApproval')"
      :show-approve-button="true"
      :show-reject-button="true"
      :action-loading="actionLoading"
      :require-reject-reason="workflow?.tenant === Tenant.CZ"
      @approve="approveStep(VehicleOrderStep.KamApproval)"
      @reject="rejectStep(VehicleOrderStep.KamApproval, $event?.reason)"
    />
    
    <WorkflowActionCard
      v-if="[VehicleOrderStep.ODApproval].includes(workflow.purchaseOrders?.[purchaseOrderIndex]?.currentProcessStep) && [Permission.DeliveryManager, Permission.DeliverySpecialist].some(p => userStore.hasPermission(p)) && (isUserAssignedToStep || isRoleAssignedToStep)"
      :card-message="t('processStatus.actionRequiredVehicleOrderStepODApproval')"
      :show-approve-button="true"
      :show-reject-button="true"
      :action-loading="actionLoading"
      :require-reject-reason="workflow?.tenant === Tenant.CZ"
      @approve="approveStep(VehicleOrderStep.ODApproval)"
      @reject="rejectStep(VehicleOrderStep.ODApproval, $event?.reason)"
    >
      <template #inputs>
        <div class="px-2 py-3 mb-2 rounded-md bg-green-50 dark:bg-green-900/20 ring-1 ring-inset ring-green-200/60 dark:ring-green-800/60 w-2xl">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div class="flex items-center gap-2 mb-1">
                <UIcon name="i-heroicons-building-storefront" class="w-4 h-4 text-green-600 dark:text-green-400" />
                <span class="text-xs text-gray-600 dark:text-gray-400">{{ t('workflowDetail.supplier.name') }}</span>
              </div>
              <p class="text-sm font-semibold text-gray-900 dark:text-white">
                {{ workflow.purchaseOrders?.[purchaseOrderIndex]?.supplier?.name || workflow.quotation?.dealer?.name || '-' }}
              </p>
            </div>
            <div>
              <div class="flex items-center gap-2 mb-1">
                <UIcon name="i-heroicons-hashtag" class="w-4 h-4 text-green-600 dark:text-green-400" />
                <span class="text-xs text-gray-600 dark:text-gray-400">{{ t('workflowDetail.supplier.id') }}</span>
              </div>
              <p class="text-sm font-mono font-semibold text-gray-900 dark:text-white">
                {{ workflow.purchaseOrders?.[purchaseOrderIndex]?.supplier?.id || workflow.quotation?.dealer?.id || '-' }}
              </p>
            </div>
            <div class="sm:col-span-2">
              <div class="flex items-center gap-2 mb-1">
                <UIcon name="i-heroicons-at-symbol" class="w-4 h-4 text-green-600 dark:text-green-400" />
                <span class="text-xs text-gray-600 dark:text-gray-400">{{ t('workflowDetail.supplier.email') }}</span>
              </div>
              <div class="space-y-1">
                <template v-if="workflow.purchaseOrders?.[purchaseOrderIndex]?.contactEmailForPO?.length">
                  <div v-for="(email, idx) in workflow.purchaseOrders?.[purchaseOrderIndex]?.contactEmailForPO" :key="idx" class="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-50 border border-green-200 text-green-700 dark:bg-green-900/40 dark:border-green-800 dark:text-green-200">
                    {{ email }}
                  </div>
                </template>
                <div v-else class="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-50 border border-green-200 text-green-700 dark:bg-green-900/40 dark:border-green-800 dark:text-green-200">-</div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </WorkflowActionCard>
    
    <WorkflowActionCard
      v-if="[VehicleOrderStep.DealerAcceptation].includes(workflow.purchaseOrders?.[purchaseOrderIndex]?.currentProcessStep) && [Permission.VehicleSupplier, Permission.SupplierSuperUser, Permission.PurchaseSupport, Permission.Dealer].some(p => userStore.hasPermission(p)) && (isUserAssignedToStep || isRoleAssignedToStep)"
      :card-message="t('processStatus.actionRequiredVehicleOrderStepDealerAcceptation')"
      :show-approve-button="true"
      :approve-label="t('processStatus.actions.acceptOrder')"
      :show-reject-button="workflow?.tenant === Tenant.CZ"
      :action-loading="actionLoading"
      :require-reject-reason="workflow?.tenant === Tenant.CZ"
      @approve="approveStep(VehicleOrderStep.DealerAcceptation)"
      @reject="rejectStep(VehicleOrderStep.DealerAcceptation, $event?.reason)"
    />
    
    <WorkflowActionCard
      v-if="[VehicleOrderStep.DealerConfirmation].includes(workflow.purchaseOrders?.[purchaseOrderIndex]?.currentProcessStep) && [Permission.VehicleSupplier, Permission.SupplierSuperUser, Permission.PurchaseSupport, Permission.Dealer, Permission.DeliveryManager, Permission.DeliverySpecialist].some(p => userStore.hasPermission(p)) && (isUserAssignedToStep || isRoleAssignedToStep)"
      :card-message="t('processStatus.actionRequiredVehicleOrderStepDealerConfirmation')"
      :show-approve-button="true"
      :approve-label="t('processStatus.actions.confirmOrder')"
      :show-reject-button="workflow?.tenant !== Tenant.FI"
      :show-close-button="canCloseDetail"
      :approve-disabled="showExpectedDeliveryDateInvalidError || (workflow?.tenant === Tenant.CZ && !dealerExpectedDeliveryDate)"
      :action-loading="actionLoading"
      :require-reject-reason="workflow?.tenant === Tenant.CZ"
      @approve="approveStep(VehicleOrderStep.DealerConfirmation)"
      @reject="rejectStep(VehicleOrderStep.DealerConfirmation, $event?.reason)"
      @close="emit('close')"
    >
      <template #inputs>
        <div class="pb-2">
          <div class="relative">
            <UPopover v-model:open="isDealerExpectedDeliveryDatePopoverOpen">
              <UButton
                color="neutral"
                variant="outline"
                class="w-48 text-sm justify-start cursor-pointer text-left focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-inverted ring ring-inset ring-inverted"
                :class="[dealerExpectedDeliveryDate ? '' : 'text-dimmed', showExpectedDeliveryDateInvalidError ? 'ring-orange-500 border-orange-500' : '']"
              >
                <UIcon name="i-lucide-calendar" class="w-4 h-4 mr-0.5"/>
                {{ dealerExpectedDeliveryDate ? formatDate(dealerExpectedDeliveryDate, 'short', locale) : t('workflowDetail.common.select')  }}
              </UButton>
              <template #content>
                <UCalendar v-model="dealerExpectedDeliveryDate" :locale="locale" :min-value="dealerExpectedDeliveryDateMinDate" :is-date-disabled="dealerExpectedDeliveryDateDisabledDates" :year-controls="false" @update:model-value="isDealerExpectedDeliveryDatePopoverOpen = false" />
              </template>
            </UPopover>
            <label class="pointer-events-none absolute left-0 -top-2.5 text-highlighted text-xs font-medium px-1.5 transition-all peer-focus:-top-2.5 peer-focus:text-highlighted peer-focus:text-xs peer-focus:font-medium peer-placeholder-shown:text-sm peer-placeholder-shown:text-dimmed peer-placeholder-shown:top-1.5 peer-placeholder-shown:font-medium">
              <span class="inline-flex bg-default px-1 rounded">{{ t('processStatus.dealerExpectedDeliveryDate') }}</span>
            </label>
          </div>
        </div>
        
        <div v-if="showExpectedDeliveryDateInvalidError" class="mt-1 mb-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div class="flex items-start gap-2">
            <UIcon name="i-heroicons-exclamation-circle" class="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div class="text-sm text-red-700 dark:text-red-300">
              <p class="font-medium mb-1">{{ t('processStatus.expectedDeliveryDateInvalidTitle') }}</p>
              <p>{{ t('processStatus.expectedDeliveryDateInvalidMessage') }}</p>
            </div>
          </div>
        </div>
      </template>
    </WorkflowActionCard>
    
    <WorkflowActionCard
      v-if="[VehicleOrderStep.DealerRejectionConfirmation].includes(workflow.purchaseOrders?.[purchaseOrderIndex]?.currentProcessStep) && [Permission.Dealer, Permission.DeliveryManager, Permission.DeliverySpecialist, Permission.CustomerCare, Permission.PurchaseSupport, Permission.ClientAccount, Permission.KeyAccountManager, Permission.Backoffice].some(p => userStore.hasPermission(p)) && (isUserAssignedToStep || isRoleAssignedToStep)"
      :card-message="t('processStatus.actionRequiredVehicleOrderStepDealerRejectionConfirmation')"
      :show-approve-button="true"
      :action-loading="actionLoading"
      @approve="approveStep(VehicleOrderStep.DealerRejectionConfirmation)"
    />
    
    <WorkflowActionCard
      v-if="[VehicleOrderStep.DealerRejection].includes(workflow.purchaseOrders?.[purchaseOrderIndex]?.currentProcessStep) && isVehicleOrderType(workflow.purchaseOrders?.[purchaseOrderIndex]?.type) && [Permission.CustomerCare, Permission.PurchaseSupport, Permission.ClientAccount, Permission.KeyAccountManager, Permission.Backoffice].some(p => userStore.hasPermission(p)) && (isUserAssignedToStep || isRoleAssignedToStep)" 
      :card-message="t('processStatus.actionRequiredVehicleOrderStepDealerRejection')"
      :show-approve-button="true"
      :action-loading="actionLoading"
      @approve="approveStep(VehicleOrderStep.DealerRejection)"
    />
    
    <WorkflowActionCard
      v-if="[TyreOrderStep.SupplierApproval].includes(workflow.purchaseOrders?.[purchaseOrderIndex]?.currentProcessStep) && workflow.purchaseOrders?.[purchaseOrderIndex]?.type === PurchaseOrder.Tyre && [Permission.TyreSupplier].some(p => userStore.hasPermission(p)) && (isUserAssignedToStep || isRoleAssignedToStep)"
      :card-message="t('processStatus.actionRequiredTyreOrderStepSupplierApproval')"
      :show-approve-button="true"
      :show-reject-button="false"
      :action-loading="actionLoading"
      @approve="approveStep(TyreOrderStep.SupplierApproval)"
      @reject="rejectStep(TyreOrderStep.SupplierApproval)"
    />
    
    <WorkflowActionCard
      v-if="[TyreOrderStep.SupplierAcceptation].includes(workflow.purchaseOrders?.[purchaseOrderIndex]?.currentProcessStep) && workflow.purchaseOrders?.[purchaseOrderIndex]?.type === PurchaseOrder.Tyre && [Permission.TyreSupplier].some(p => userStore.hasPermission(p)) && (isUserAssignedToStep || isRoleAssignedToStep)"
      :card-message="t('processStatus.actionRequiredTyreOrderStepSupplierAcceptation')"
      :show-approve-button="true"
      :show-reject-button="false"
      :action-loading="actionLoading"
      @approve="approveStep(TyreOrderStep.SupplierAcceptation)"
      @reject="rejectStep(TyreOrderStep.SupplierAcceptation)"
    />
    
    <WorkflowActionCard
      v-if="[TyreOrderStep.DeliveryConfirmation].includes(workflow.purchaseOrders?.[purchaseOrderIndex]?.currentProcessStep) && workflow.purchaseOrders?.[purchaseOrderIndex]?.type === PurchaseOrder.Tyre && [Permission.TyreSupplier].some(p => userStore.hasPermission(p)) && (isUserAssignedToStep || isRoleAssignedToStep)"
      :card-message="t('processStatus.actionRequiredTyreOrderStepDeliveryConfirmation')"
      :show-submit-button="true"
      :submit-disabled="!secondTyreSetDeliveryDate || actionLoading"
      :action-loading="actionLoading"
      @submit="submitDeliveryInfo(TyreOrderStep.DeliveryConfirmation)"
    >
      <template #inputs>
        <div class="pb-3">
          <div class="relative">
            <UPopover v-model:open="isSecondTyreSetDeliveryDatePopoverOpen">
              <UButton
                color="neutral"
                variant="outline"
                class="w-48 text-sm justify-start cursor-pointer text-left focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-inverted ring ring-inset ring-inverted"
                :class="[secondTyreSetDeliveryDate ? '' : 'text-dimmed']"
              >
                <UIcon name="i-lucide-calendar" class="w-4 h-4 mr-0.5"/>
                {{ secondTyreSetDeliveryDate ? formatDate(secondTyreSetDeliveryDate, 'short', locale) : t('workflowDetail.common.select') }}
              </UButton>
              <template #content>
                <UCalendar v-model="secondTyreSetDeliveryDate" :locale="locale" :min-value="dealerExpectedDeliveryDateMinDate" :is-date-disabled="dealerExpectedDeliveryDateDisabledDates" :year-controls="false" @update:model-value="isSecondTyreSetDeliveryDatePopoverOpen = false" />
              </template>
            </UPopover>
            <label class="pointer-events-none absolute left-0 -top-2.5 text-highlighted text-xs font-medium px-1.5 transition-all peer-focus:-top-2.5 peer-focus:text-highlighted peer-focus:text-xs peer-focus:font-medium peer-placeholder-shown:text-sm peer-placeholder-shown:text-dimmed peer-placeholder-shown:top-1.5 peer-placeholder-shown:font-medium">
              <span class="inline-flex bg-default px-1 rounded">{{ t('processStatus.secondTyreSetDeliveryDate') }}</span>
            </label>
          </div>
        </div>
        <div class="pb-2">
          <div class="relative">
            <UInput
              v-model="secondTyreSetDeliveryInfo"
              color="neutral"
              highlight
              placeholder=""
              :maxlength="30"
              class="w-[32rem] h-7 text-sm"
              :ui="{ base: 'peer', trailing: 'pointer-events-none' }"
            >
              <template #trailing>
                <div class="text-sm text-muted tabular-nums">
                  {{ secondTyreSetDeliveryInfo?.length || 0 }}/{{ 30 }}
                </div>
              </template>
              <label class="pointer-events-none absolute left-0 -top-2.5 text-highlighted text-xs font-medium px-1.5 transition-all peer-focus:-top-2.5 peer-focus:text-highlighted peer-focus:text-xs peer-focus:font-medium peer-placeholder-shown:text-sm peer-placeholder-shown:text-dimmed peer-placeholder-shown:top-1.5 peer-placeholder-shown:font-medium">
                <span class="inline-flex bg-default px-1 rounded">{{ t('processStatus.secondTyreSetDeliveryInfo') }}</span>
              </label>
            </UInput>
          </div>
        </div>
      </template>
    </WorkflowActionCard>
    
    <WorkflowActionCard
      v-if="[TyreOrderStep.Rejection].includes(workflow.purchaseOrders?.[purchaseOrderIndex]?.currentProcessStep) && workflow.purchaseOrders?.[purchaseOrderIndex]?.type === PurchaseOrder.Tyre && [Permission.CustomerCare, Permission.PurchaseSupport].some(p => userStore.hasPermission(p)) && (isUserAssignedToStep || isRoleAssignedToStep)"
      :card-message="t('processStatus.actionRequiredTyreOrderStepRejection')"
      :show-approve-button="true"
      :action-loading="actionLoading"
      @approve="approveStep(TyreOrderStep.Rejection)"
    />
    
    <WorkflowActionCard
      v-if="[WorkflowStatus.OrderProcessing, WorkflowStatus.OrderUpdated].includes(workflow.status) && isVehicleOrderType(workflow.purchaseOrders?.[purchaseOrderIndex]?.type) && [DeliveryStep.Waiting].includes(workflow.delivery?.currentProcessStep) && [Permission.VehicleSupplier, Permission.SupplierSuperUser, Permission.PurchaseSupport, Permission.Dealer, Permission.DeliveryManager, Permission.DeliverySpecialist].some(p => userStore.hasPermission(p)) && (isUserAssignedToStep || isRoleAssignedToStep)"
      :card-message="t('processStatus.actionRequiredDeliveryStepWaiting')"
      :show-submit-button="true"
      :submit-disabled="(workflow?.tenant === Tenant.CZ && (vinNumber === '' || vinNumber.length < 17 || actionLoading)) || (workflow?.tenant === Tenant.FI && (vinNumber === '' || vinNumber.length < 17 || licensePlate === '' || !dealerExpectedDeliveryDate || actionLoading || showExpectedDeliveryDateInvalidError))"
      :action-loading="actionLoading"
      @submit="submitDeliveryInfo(DeliveryStep.Waiting)"
    >
      <template #inputs>
        <div class="flex flex-wrap gap-2 space-y-2">
          <div class="relative">
            <UInput
              v-model="vinNumber"
              color="neutral"
              highlight
              placeholder=""
              class="w-54 h-7 text-sm"
              :maxlength="17"
              :ui="{ base: 'peer uppercase', trailing: 'pointer-events-none' }"
            >
              <template #trailing>
                <div class="text-sm text-muted tabular-nums">
                  {{ vinNumber?.length }}/{{ 17 }}
                </div>
              </template>
              <label class="pointer-events-none absolute left-0 -top-2.5 text-highlighted text-xs font-medium px-1.5 transition-all peer-focus:-top-2.5 peer-focus:text-highlighted peer-focus:text-xs peer-focus:font-medium peer-placeholder-shown:text-sm peer-placeholder-shown:text-dimmed peer-placeholder-shown:top-1.5 peer-placeholder-shown:font-medium">
                <span class="inline-flex bg-default px-1 rounded">{{ t('processStatus.vin') }}</span>
              </label>
            </UInput>
          </div>
          <div class="relative" v-if="workflow?.tenant === Tenant.CZ">
            <UInput
              v-model="co2Emissions"
              type="number"
              color="neutral"
              highlight
              placeholder=""
              class="w-32 h-7 text-xs"
              :ui="{ base: 'peer' }"
            >
              <label class="pointer-events-none absolute left-0 -top-2.5 text-highlighted text-xs font-medium px-1.5 transition-all peer-focus:-top-2.5 peer-focus:text-highlighted peer-focus:text-xs peer-focus:font-medium peer-placeholder-shown:text-sm peer-placeholder-shown:text-dimmed peer-placeholder-shown:top-1.5 peer-placeholder-shown:font-medium">
                <span class="inline-flex bg-default px-1 rounded">{{ t('processStatus.co2Emissions') }}</span>
              </label>
            </UInput>
          </div>
          <div class="relative" v-if="workflow?.tenant === Tenant.FI">
            <UInput
              v-model="licensePlate"
              color="neutral"
              highlight
              placeholder=""
              class="w-44 h-7 text-xs"
              :ui="{ base: 'peer uppercase' }"
            >
              <label class="pointer-events-none absolute left-0 -top-2.5 text-highlighted text-xs font-medium px-1.5 transition-all peer-focus:-top-2.5 peer-focus:text-highlighted peer-focus:text-xs peer-focus:font-medium peer-placeholder-shown:text-sm peer-placeholder-shown:text-dimmed peer-placeholder-shown:top-1.5 peer-placeholder-shown:font-medium">
                <span class="inline-flex bg-default px-1 rounded">{{ t('processStatus.registrationNumber') }}</span>
              </label>
            </UInput>
          </div>
          <div class="relative" v-if="workflow?.tenant === Tenant.FI">
            <UPopover v-model:open="isDealerExpectedDeliveryDatePopoverOpen">
              <UButton
                color="neutral"
                variant="outline"
                class="w-48 text-sm justify-start cursor-pointer text-left focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-inverted ring ring-inset ring-inverted"
                :class="[dealerExpectedDeliveryDate ? '' : 'text-dimmed', showExpectedDeliveryDateInvalidError ? 'ring-orange-500 border-orange-500' : '']"
              >
                <UIcon name="i-lucide-calendar" class="w-4 h-4 mr-0.5"/>
                {{ dealerExpectedDeliveryDate ? formatDate(dealerExpectedDeliveryDate, 'short', locale) : t('workflowDetail.common.select')  }}
              </UButton>
              <template #content>
                <UCalendar v-model="dealerExpectedDeliveryDate" :locale="locale" :min-value="dealerExpectedDeliveryDateMinDate" :is-date-disabled="dealerExpectedDeliveryDateDisabledDates" :year-controls="false" @update:model-value="isDealerExpectedDeliveryDatePopoverOpen = false" />
              </template>
            </UPopover>
            <label class="pointer-events-none absolute left-0 -top-2.5 text-highlighted text-xs font-medium px-1.5 transition-all peer-focus:-top-2.5 peer-focus:text-highlighted peer-focus:text-xs peer-focus:font-medium peer-placeholder-shown:text-sm peer-placeholder-shown:text-dimmed peer-placeholder-shown:top-1.5 peer-placeholder-shown:font-medium">
              <span class="inline-flex bg-default px-1 rounded">{{ t('processStatus.dealerExpectedDeliveryDate') }}</span>
            </label>
          </div>
        </div>
        
        <div v-if="showExpectedDeliveryDateInvalidError" class="mt-1 mb-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div class="flex items-start gap-2">
            <UIcon name="i-heroicons-exclamation-circle" class="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div class="text-sm text-red-700 dark:text-red-300">
              <p class="font-medium mb-1">{{ t('processStatus.expectedDeliveryDateInvalidTitle') }}</p>
              <p>{{ t('processStatus.expectedDeliveryDateInvalidMessage') }}</p>
            </div>
          </div>
        </div>
      </template>
    </WorkflowActionCard>
    
    <WorkflowActionCard
      v-if="workflow?.tenant === Tenant.CZ && [WorkflowStatus.Delivery].includes(workflow.status) && isVehicleOrderType(workflow.purchaseOrders?.[purchaseOrderIndex]?.type) && [DeliveryStep.VehicleRegistration].includes(workflow.delivery?.currentProcessStep) && [Permission.DeliveryManager, Permission.DeliverySpecialist].some(p => userStore.hasPermission(p)) && (isUserAssignedToStep || isRoleAssignedToStep)"
      :card-message="t('processStatus.actionRequiredDeliveryStepVehicleRegistration')"
      :show-submit-button="true"
      :submit-disabled="licensePlate === '' || !registrationDate || actionLoading"
      :action-loading="actionLoading"
      @submit="submitDeliveryInfo(DeliveryStep.VehicleRegistration)"
    >
      <template #inputs>
        <div class="flex flex-wrap gap-2 space-y-2">
          <div class="relative">
            <UInput
              v-model="licensePlate"
              color="neutral"
              highlight
              placeholder=""
              :disabled="workflow?.client?.sourceSystem === SourceSystem.NOLS"
              class="w-44 h-7 text-xs"
              :ui="{ base: 'peer uppercase' }"
            >
              <label class="pointer-events-none absolute left-0 -top-2.5 text-highlighted text-xs font-medium px-1.5 transition-all peer-focus:-top-2.5 peer-focus:text-highlighted peer-focus:text-xs peer-focus:font-medium peer-placeholder-shown:text-sm peer-placeholder-shown:text-dimmed peer-placeholder-shown:top-1.5 peer-placeholder-shown:font-medium">
                <span class="inline-flex bg-default px-1 rounded">{{ t('processStatus.registrationNumber') }}</span>
              </label>
            </UInput>
          </div>
          <div class="relative">
            <UPopover v-model:open="isRegistrationDatePopoverOpen">
              <UButton
                color="neutral"
                :disabled="workflow?.client?.sourceSystem === SourceSystem.NOLS"
                variant="outline"
                class="w-42 text-sm justify-start cursor-pointer text-left focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-inverted ring ring-inset ring-inverted"
                :class="registrationDate ? '' : 'text-dimmed'"
              >
                <UIcon name="i-lucide-calendar" class="w-4 h-4 mr-0.5"/>
                {{ registrationDate ? formatDate(registrationDate, 'short', locale) : t('workflowDetail.common.select')  }}
              </UButton>
              <template #content>
                <UCalendar v-model="registrationDate" :locale="locale" :year-controls="false" @update:model-value="isRegistrationDatePopoverOpen = false" />
              </template>
            </UPopover>
            <label class="pointer-events-none absolute left-0 -top-2.5 text-highlighted text-xs font-medium px-1.5 transition-all peer-focus:-top-2.5 peer-focus:text-highlighted peer-focus:text-xs peer-focus:font-medium peer-placeholder-shown:text-sm peer-placeholder-shown:text-dimmed peer-placeholder-shown:top-1.5 peer-placeholder-shown:font-medium">
              <span class="inline-flex bg-default px-1 rounded">{{ t('processStatus.registrationDate') }}</span>
            </label>
          </div>
        </div>
      </template>
    </WorkflowActionCard>
    
    <!--
    <WorkflowActionCard
      v-if="workflow?.tenant === Tenant.FI && [WorkflowStatus.Delivery].includes(workflow.status) && isVehicleOrderType(workflow.purchaseOrders?.[purchaseOrderIndex]?.type) && [DeliveryStep.HandoverPreparation].includes(workflow.delivery?.currentProcessStep) && [Permission.VehicleSupplier, Permission.SupplierSuperUser, Permission.DeliveryManager, Permission.DeliverySpecialist].some(p => userStore.hasPermission(p)) && (isUserAssignedToStep || isRoleAssignedToStep)"
      :card-message="t('processStatus.actionRequiredDeliveryStepHandoverPreparation')"
      :show-submit-button="true"
      :action-loading="actionLoading"
      @submit="approveStep(DeliveryStep.HandoverPreparation)"
    />
    -->
    
    <WorkflowActionCard
      v-if="[DeliveryStep.Rejection].includes(workflow.delivery?.currentProcessStep) && deliveryRejectPermissions.some(p => userStore.hasPermission(p)) && (isUserAssignedToStep || isRoleAssignedToStep)"
      :card-message="t('processStatus.actionRequiredDeliveryStepRejection')"
      :show-approve-button="true"
      :action-loading="actionLoading"
      @approve="confirmDeliveryRejection()"
    />
    
    <WorkflowActionCard
      v-if="[WorkflowStatus.Delivery].includes(workflow.status) && isVehicleOrderType(workflow.purchaseOrders?.[purchaseOrderIndex]?.type) && [DeliveryStep.Handover].includes(workflow.delivery?.currentProcessStep) && [Permission.CustomerCare, Permission.VehicleSupplier, Permission.SupplierSuperUser, Permission.PurchaseSupport, Permission.DeliveryManager, Permission.DeliverySpecialist].some(p => userStore.hasPermission(p)) && (isUserAssignedToStep || isRoleAssignedToStep)"
      :card-message="t('processStatus.actionRequiredDeliveryStepHandover')"
      :show-submit-button="true"
      :submit-disabled="!handoverDate || handoverMileage === '' || actionLoading || showHandoverDateMustBeTodayError"
      :action-loading="actionLoading"
      @submit="submitDeliveryInfo(DeliveryStep.Handover)"
    >
      <template #inputs>
        <div class="flex flex-wrap gap-2 space-y-2">
          <div class="relative">
            <UPopover v-model:open="isHandoverDatePopoverOpen">
              <UButton
                color="neutral"
                variant="outline"
                class="w-42 text-sm justify-start cursor-pointer text-left focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-inverted ring ring-inset ring-inverted"
                :class="[handoverDate ? '' : 'text-dimmed', showHandoverDateMustBeTodayError ? 'ring-orange-500 border-orange-500' : '']"
              >
                <UIcon name="i-lucide-calendar" class="w-4 h-4 mr-0.5"/>
                {{ handoverDate ? formatDate(handoverDate, 'short', locale) : t('workflowDetail.common.select')  }}
              </UButton>
              <template #content>
                <UCalendar v-model="handoverDate" :locale="locale" :year-controls="false" @update:model-value="isHandoverDatePopoverOpen = false" />
              </template>
            </UPopover>
            <label class="pointer-events-none absolute left-0 -top-2.5 text-highlighted text-xs font-medium px-1.5 transition-all peer-focus:-top-2.5 peer-focus:text-highlighted peer-focus:text-xs peer-focus:font-medium peer-placeholder-shown:text-sm peer-placeholder-shown:text-dimmed peer-placeholder-shown:top-1.5 peer-placeholder-shown:font-medium">
              <span class="inline-flex bg-default px-1 rounded">{{ workflow?.tenant === Tenant.FI ? t('processStatus.registrationDate') : t('processStatus.handoverDate') }}</span>
            </label>
          </div>
          <div class="relative">
            <UInput
              v-model="handoverMileage"
              type="number"
              color="neutral"
              highlight
              placeholder=""
              class="w-38 h-7 text-xs"
              :ui="{ base: 'peer' }"
            >
              <label class="pointer-events-none absolute left-0 -top-2.5 text-highlighted text-xs font-medium px-1.5 transition-all peer-focus:-top-2.5 peer-focus:text-highlighted peer-focus:text-xs peer-focus:font-medium peer-placeholder-shown:text-sm peer-placeholder-shown:text-dimmed peer-placeholder-shown:top-1.5 peer-placeholder-shown:font-medium">
                <span class="inline-flex bg-default px-1 rounded">{{ workflow?.tenant === Tenant.FI ? t('processStatus.initialMileage') : t('processStatus.handoverMileage') }}</span>
              </label>
            </UInput>
          </div>
        </div>
        
        <div v-if="showHandoverDateMustBeTodayError" class="mt-1 mb-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div class="flex items-start gap-2">
            <UIcon name="i-heroicons-exclamation-circle" class="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div class="text-sm text-red-700 dark:text-red-300">
              <p>{{ t('processStatus.registrationDateMustBeTodayMessage') }}</p>
            </div>
          </div>
        </div>
      </template>
    </WorkflowActionCard>
  </UCard>
  
  <WorkflowApprovalModal
    v-if="workflow?.externalReference"
    v-model="isWorkflowApprovalModalOpen"
    :workflow-id="workflow.externalReference"
    :client-id="workflow?.clientId || workflow?.client?.id || null"
    @saved="$emit('reload')"
  />
</template>

<script setup lang="ts">
import { ref, resolveComponent, computed, onMounted, watch } from 'vue'
import jsonpatch from 'fast-json-patch'
import { api, type WorkflowDto, Initiator, WorkflowStatus, QuotationStep, VehicleOrderStep, TyreOrderStep, PersonPartner, DriverDto, DeliveryStep, EventLog, type ResendResultDto, type SsnDto, UnparkScenario, UnparkOrderScenario } from '@/api/eOrdersApi'
import { useI18n } from "vue-i18n";
import { showSuccess, showWarning, showError, showValidationErrors } from "@/utils/utils.ts";
import { formatDate, addBusinessDays } from "@/utils/date.ts";
import { isPhoneNumberValid } from "@/utils/phone";
import PhoneInput from "@/components/PhoneInput.vue";
import { useApiAdapter, useUser } from "@/store";
import type { ApiAdapterResult } from "@/store/apiAdapter";
import WorkflowActionCard from "@/components/workflows/WorkflowActionCard.vue";
import WorkflowApprovalModal from "@/components/workflows/WorkflowApprovalModal.vue";
import ApprovalFlowVisualization from "@/components/workflows/ApprovalFlowVisualization.vue";
import { Tenant } from "@/types/enums/Tenant.ts";
import { SourceSystem } from "@/types/enums/SourceSystem.ts";
import { PurchaseOrder, isVehicleOrderType } from "@/types/enums/PurchaseOrder.ts";
import { Permission } from "@/types/enums/Permission.ts";
import { isCzechiaQuotation, isNolsQuotation } from "@/types/discriminators";
import { CalendarDate, type DateValue } from "@internationalized/date";

const UButton = resolveComponent('UButton')
const UIcon = resolveComponent('UIcon')
const UCard = resolveComponent('UCard')
const UInput = resolveComponent('UInput')
const UPopover = resolveComponent('UPopover')
const UCalendar = resolveComponent('UCalendar')

const { t, locale } = useI18n()
const userStore = useUser();
const apiAdapter = useApiAdapter()

const props = defineProps<{
  workflow: WorkflowDto | null;
  driver?: DriverDto | null;
  driverLoading?: boolean;
  drivers?: PersonPartner[];
  purchaseOrderIndex: number;
  purchaseOrderNumber: string | null;
  slug?: string;
  slugToken?: string;
  canCloseDetail?: boolean;
}>();

const emit = defineEmits(['reload', 'close']);

const workflow = computed(() => props.workflow);
const actionLoading = ref(false);
const isWorkflowApprovalModalOpen = ref(false);
const notRegisteredUserResendLoading = ref(false);

const selectedDriverOption = ref(workflow.value?.driverId || undefined)
const existingDriverContact = (workflow.value as any)?.driverContact
const clientFilledDriverContact = computed(() => (workflow.value as any)?.driverContact ?? null)

const showClientFilledDriver = computed(() => {
  const contact = clientFilledDriverContact.value
  if (!contact) return false
  if (workflow.value?.tenant !== Tenant.CZ) return false
  
  const step = workflow.value?.quotation?.currentProcessStep
  if (step !== QuotationStep.ClientAccountAcceptation && step !== QuotationStep.OrderCreation) return false
  
  return [Permission.ClientAccount, Permission.KeyAccountManager, Permission.Backoffice].some(p => userStore.hasPermission(p))
})

const wantsNewDriver = ref(true)
const driverFirstName = ref(existingDriverContact?.firstName ?? '')
const driverLastName = ref(existingDriverContact?.surname ?? '')
const driverPhone = ref(existingDriverContact?.phoneNumber ?? '')
const driverEmail = ref(existingDriverContact?.email ?? '')

const driverSelectOptions = computed(() => {
  const existingDrivers = (props.drivers ?? []).map(driver => ({
    label: `${driver.name}${driver.phone ? ` (${driver.phone})` : ''}${driver.email ? ` – ${driver.email}` : ''}`,
    value: driver.id
  }))
  
  return [
    ...existingDrivers,
    {
      label: 'Add a new driver',
      value: 'new'
    }
  ]
})

const isDriverFormValid = computed(() => {
  if (wantsNewDriver.value) {
    return driverFirstName.value.trim() !== '' && driverLastName.value.trim() !== '' && isPhoneNumberValid(driverPhone.value) && driverEmail.value.trim() !== '';
  } else {
    return true;
  }
})

const replacedPlate = ref(workflow.value?.replacedCar?.licensePlate || '')
const meaningRequiredDeliveryDate = ref(workflow.value?.quotation?.requestedDeliveryDateScenario?.mode || null)
const isRequiredDeliveryDatePopoverOpen = ref(false)
const requiredDeliveryDateMinDate = new CalendarDate(new Date().getFullYear(), new Date().getMonth() + 1, new Date().getDate())

const requiredDeliveryDateDisabledDates = (date: DateValue) => {
  const jsDate = new Date(date.year, date.month - 1, date.day);
  const dayOfWeek = jsDate.getDay();
  
  let isDisabled = dayOfWeek === 0 || dayOfWeek === 6;
  
  if (replacedPlate.value && replacedPlate.value.trim() !== '') {
    const year = date.year;
    const dec15 = new Date(year, 11, 15);
    const dec31 = new Date(year, 11, 31);

    if (jsDate >= dec15 && jsDate <= dec31) {
      isDisabled = true;
    }
  }

  return isDisabled;
}

const parseDateString = (dateStr: string): CalendarDate => {
  const date = new Date(dateStr)
  return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate())
}

const requiredDeliveryDate = ref(
  (() => {
    const dateString = workflow.value?.quotation?.requestedDeliveryDateScenario?.date
    return dateString ? parseDateString(dateString) : null
  })()
)

const frameworkPurchaseAgreement = ref('')
const isSsnFlow = computed(() => props.driver?.$type === 'DriverSsnDto')
const socialSecurityNumber = ref(props.driver?.ssn ?? '')
const initialQuotationNumber = ref(workflow.value?.quotation?.initialQuotationNumber || '')
const vinNumber = ref(workflow.value?.delivery?.vin || '')
const licensePlate = ref(workflow.value?.delivery?.licensePlate || '')
const co2Emissions = ref(workflow.value?.delivery?.cO2 ?? '')

const isDealerExpectedDeliveryDatePopoverOpen = ref(false)
const dealerExpectedDeliveryDateMinDate = new CalendarDate(new Date().getFullYear(), new Date().getMonth() + 1, new Date().getDate())

const dealerExpectedDeliveryDateDisabledDates = (date: DateValue) => {
  const jsDate = new Date(date.year, date.month - 1, date.day);
  const dayOfWeek = jsDate.getDay();

  return dayOfWeek === 0 || dayOfWeek === 6
}

const dealerExpectedDeliveryDate = ref(
  (() => {
    const dateString = workflow.value?.purchaseOrders?.[props.purchaseOrderIndex]?.dealerExpectedDeliveryDate
    return dateString ? parseDateString(dateString) : null
  })()
)

const isRegistrationDatePopoverOpen = ref(false)

const registrationDate = ref(
  (() => {
    const dateString = workflow.value?.delivery?.registrationDate
    return dateString ? parseDateString(dateString) : null
  })()
)

const isHandoverDatePopoverOpen = ref(false)

const handoverDate = ref(
  (() => {
    const dateString = workflow.value?.delivery?.handoverDate
    if (dateString) return parseDateString(dateString)
    if (workflow.value?.tenant === Tenant.FI) {
      const now = new Date()
      return new CalendarDate(now.getFullYear(), now.getMonth() + 1, now.getDate())
    }
    return null
  })()
)

const handoverMileage = ref(workflow.value?.delivery?.handoverMileage ?? '')

const secondTyreSetDeliveryInfo = ref('')
const isSecondTyreSetDeliveryDatePopoverOpen = ref(false)
const secondTyreSetDeliveryDate = ref(
  (() => {
    const dateString = workflow.value?.purchaseOrders?.[props.purchaseOrderIndex]?.deliveryDate
    return dateString ? parseDateString(dateString) : null
  })()
)

const isExpectedDeliveryDateInvalid = computed(() => {
  if (!dealerExpectedDeliveryDate.value) return false;
  
  const { year, month, day } = dealerExpectedDeliveryDate.value;
  const expectedDate = new Date(year, month - 1, day);
  expectedDate.setHours(0, 0, 0, 0);
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const minBusinessDate = addBusinessDays(today, 7);
  
  if (expectedDate <= minBusinessDate) return true;
  
  const possibleDeliveryDateStr = workflow.value?.quotation?.requestedDeliveryDateScenario?.date;
  
  if (possibleDeliveryDateStr) {
    const possibleDate = new Date(possibleDeliveryDateStr);
    possibleDate.setHours(0, 0, 0, 0);
    
    if (expectedDate < possibleDate) return true;
  }

  return false;
});

const showExpectedDeliveryDateInvalidError = computed(() => {
  return workflow.value?.tenant === Tenant.FI && isExpectedDeliveryDateInvalid.value && userStore.hasPermission(Permission.VehicleSupplier) && !userStore.hasPermission(Permission.PurchaseSupport);
});

const showHandoverDateMustBeTodayError = computed(() => {
  if (workflow.value?.tenant !== Tenant.FI) return false;
  if (!userStore.hasPermission(Permission.VehicleSupplier)) return false;
  if (userStore.hasPermission(Permission.PurchaseSupport)) return false;
  if (!handoverDate.value) return false;
  
  const { year, month, day } = handoverDate.value;
  const selected = new Date(year, month - 1, day);
  selected.setHours(0, 0, 0, 0);
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  return selected.getTime() !== today.getTime();
});

const orderProgressSteps = computed(() => [
  {
    id: WorkflowStatus.QuotationApproval,
    label: t('processStatus.status.quotationApproval'),
    icon: 'i-mdi-file-document-edit',
    borderClass: 'border-l-blue-500',
    order: 1
  },
  {
    id: WorkflowStatus.OrderApproval,
    label: t('processStatus.status.orderApproval'),
    icon: 'i-mdi-file-check',
    borderClass: 'border-l-blue-500',
    order: 2
  },
  {
    id: WorkflowStatus.OrderProcessing,
    label: t('processStatus.status.orderProcessing'),
    icon: 'i-mdi-cog-transfer',
    borderClass: 'border-l-blue-500',
    order: 3
  },
  {
    id: WorkflowStatus.Delivery,
    label: t('processStatus.status.delivery'),
    icon: 'i-mdi-truck-delivery',
    borderClass: 'border-l-blue-500',
    order: 4
  },
  {
    id: WorkflowStatus.Completed,
    label: t('processStatus.status.completed'),
    icon: 'i-mdi-check-circle',
    borderClass: 'border-l-green-500',
    order: 5
  },
  {
    id: WorkflowStatus.QuotationRejection,
    label: t('processStatus.status.quotationRejection'),
    icon: 'i-mdi-close-circle',
    borderClass: 'border-l-red-500',
    order: -1
  },
  {
    id: WorkflowStatus.OrderRejection,
    label: t('processStatus.status.orderRejection'),
    icon: 'i-mdi-close-circle',
    borderClass: 'border-l-red-500',
    order: -1
  },
  {
    id: WorkflowStatus.DeliveryRejection,
    label: t('processStatus.status.deliveryRejection'),
    icon: 'i-mdi-close-circle',
    borderClass: 'border-l-red-500',
    order: -1
  },
  {
    id: WorkflowStatus.Rejected,
    label: t('processStatus.status.rejected'),
    icon: 'i-mdi-close-circle',
    borderClass: 'border-l-red-500',
    order: -1
  },
  {
    id: WorkflowStatus.Archived,
    label: t('processStatus.status.archived'),
    icon: 'i-mdi-archive',
    borderClass: 'border-l-gray-400',
    order: -1
  }
]);

const tyreOrderProgressSteps = computed(() => [
  {
    id: TyreOrderStep.SupplierApproval,
    label: t('processStatus.step.orderApproval.supplierApproval'),
    icon: 'i-mdi-file-check',
    borderClass: 'border-l-blue-500',
    order: 1
  },
  {
    id: TyreOrderStep.SupplierAcceptation,
    label: t('processStatus.step.orderApproval.supplierAcceptation'),
    icon: 'i-mdi-shield-check',
    borderClass: 'border-l-blue-500',
    order: 2
  },
  {
    id: TyreOrderStep.DeliveryConfirmation,
    label: t('processStatus.step.orderApproval.deliveryConfirmation'),
    icon: 'i-mdi-truck-delivery',
    borderClass: 'border-l-blue-500',
    order: 3
  },
  {
    id: TyreOrderStep.Delivered,
    label: t('processStatus.step.orderApproval.delivered'),
    icon: 'i-mdi-check-circle',
    borderClass: 'border-l-green-500',
    order: 4
  },
  {
    id: WorkflowStatus.OrderRejection,
    label: t('processStatus.status.orderRejection'),
    icon: 'i-mdi-close-circle',
    borderClass: 'border-l-red-500',
    order: -1
  },
  {
    id: WorkflowStatus.DeliveryRejection,
    label: t('processStatus.status.deliveryRejection'),
    icon: 'i-mdi-close-circle',
    borderClass: 'border-l-red-500',
    order: -1
  },
  {
    id: WorkflowStatus.Rejected,
    label: t('processStatus.status.rejected'),
    icon: 'i-mdi-close-circle',
    borderClass: 'border-l-red-500',
    order: -1
  },
  {
    id: WorkflowStatus.Archived,
    label: t('processStatus.status.archived'),
    icon: 'i-mdi-archive',
    borderClass: 'border-l-gray-400',
    order: -1
  }
]);

const isRoleAssignedToQuotationStep = computed(() => {
  const assigneeRoles = workflow.value?.quotation?.assignees?.map(assignee => {
    if (assignee.role) {
      return assignee.role;
    }
    return null;
  }).filter(id => id !== null) || [];

  const userRoleNames = userStore.getUserRoles();

  return assigneeRoles.some(requiredRole =>
    userRoleNames.some(userRole =>
      userRole === requiredRole || userRole === `role.${requiredRole}`
    )
  );
});

const isRoleAssignedToStep = computed(() => {
  const assigneeRoles = workflow.value?.assignees?.map(assignee => {
    if (assignee.role) {
      return assignee.role;
    }
    return null;
  }).filter(id => id !== null) || [];

  const userRoleNames = userStore.getUserRoles();

  return assigneeRoles.some(requiredRole =>
    userRoleNames.some(userRole =>
      userRole === requiredRole || userRole === `role.${requiredRole}`
    )
  );
});

const isUserAssignedToQuotationStep = computed(() => {
  const assigneeIds = workflow.value?.quotation?.assignees?.map(assignee => {
    if (assignee.user && assignee.user.keycloakId) {
      return assignee.user.keycloakId;
    }
    return null;
  }).filter(id => id !== null) || [];

  return assigneeIds.includes(userStore.user.id);
});

const isUserAssignedToStep = computed(() => {
  const assigneeIds = workflow.value?.assignees?.map(assignee => {
    if (assignee.user && assignee.user.keycloakId) {
      return assignee.user.keycloakId;
    }
    return null;
  }).filter(id => id !== null) || [];

  return assigneeIds.includes(userStore.user.id);
});

const isTyreOrder = computed(() => {
  return workflow.value?.purchaseOrders?.[props.purchaseOrderIndex]?.type === PurchaseOrder.Tyre;
});

const activeProgressSteps = computed(() => {
  return isTyreOrder.value ? tyreOrderProgressSteps.value : orderProgressSteps.value;
});

const currentClientApprovalStepIndex = computed(() => {
  const currentStep = workflow.value?.quotation?.currentProcessStep;
  const config = workflow.value?.quotation?.clientApprovalConfiguration;
  const backendIndex = workflow.value?.quotation?._currentClientApprovalStep ?? 0;
  
  if (currentStep === QuotationStep.OrderCreation || currentStep === QuotationStep.Completed) {
    return config?.length ?? 0;
  }
  
  if (currentStep === QuotationStep.SchemaFinalization || currentStep === QuotationStep.NotDefined) {
    return -1;
  }
  
  return Math.min(backendIndex, config?.length ?? 0);
});

const offTrackStatuses: WorkflowStatus[] = [
  WorkflowStatus.QuotationRejection,
  WorkflowStatus.OrderRejection,
  WorkflowStatus.DeliveryRejection,
  WorkflowStatus.Rejected,
  WorkflowStatus.Archived
];

const rejectionStatuses: WorkflowStatus[] = [
  WorkflowStatus.QuotationRejection,
  WorkflowStatus.OrderRejection,
  WorkflowStatus.DeliveryRejection
];

const deliveryRejectPermissions: Permission[] = [
  Permission.ClientAccount,
  Permission.DeliveryManager,
  Permission.DeliverySpecialist,
  Permission.KeyAccountManager,
  Permission.CustomerCare,
  Permission.Backoffice
];

const filteredSteps = computed(() =>
  activeProgressSteps.value.filter(step => !offTrackStatuses.includes(step.id as WorkflowStatus))
);

const workflowPhase = computed((): 'quotation' | 'order' | 'delivery' | 'unknown' => {
  const wf = workflow.value;

  switch (wf?.status) {
    case WorkflowStatus.QuotationApproval:
    case WorkflowStatus.QuotationRejection:
      return 'quotation';
      
    case WorkflowStatus.OrderApproval:
    case WorkflowStatus.OrderRejection:
      return 'order';
      
    case WorkflowStatus.OrderProcessing:
    case WorkflowStatus.OrderUpdated:
    case WorkflowStatus.Delivery:
    case WorkflowStatus.DeliveryRejection:
    case WorkflowStatus.Completed:
    case WorkflowStatus.Archived:
      return 'delivery';
      
    case WorkflowStatus.Rejected:
      if (wf.delivery?.currentProcessStep === DeliveryStep.Rejected) return 'delivery';
      if (wf.purchaseOrders?.some(po => po.currentProcessStep === 'Rejected')) return 'order';
      if (wf.quotation?.currentProcessStep === QuotationStep.Rejected) return 'quotation';
      return 'unknown';
      
    default:
      return 'unknown';
  }
});

const isQuotationPhase = computed(() => workflowPhase.value === 'quotation');

const isTyreOrderRejected = computed(() => {
  if (!isTyreOrder.value) return false;
  
  const step = workflow.value?.purchaseOrders?.[props.purchaseOrderIndex]?.currentProcessStep;
  
  return step === TyreOrderStep.Rejection || step === TyreOrderStep.Rejected;
});

const showRejectionMarker = computed(() => {
  const status = workflow.value?.status;
  
  return (!!status && rejectionStatuses.includes(status)) || status === WorkflowStatus.Rejected || isTyreOrderRejected.value;
});

const rejectionMarkerStep = computed(() => {
  const status = workflow.value?.status;
  const id = status && (rejectionStatuses.includes(status) || status === WorkflowStatus.Rejected) ? status : WorkflowStatus.OrderRejection;
  
  return activeProgressSteps.value.find(s => s.id === id) ?? null;
});

const rejectionEventLog = computed(() => {
  switch (workflowPhase.value) {
    case 'quotation':
      return getEventLogByStep(WorkflowStatus.QuotationApproval);
    case 'delivery':
      return getEventLogByStep(WorkflowStatus.Delivery);
    default:
      return getEventLogByStep(WorkflowStatus.OrderApproval);
  }
});

const getEventLogByStep = (stepId: string): EventLog | null => {
  const eventLogs = workflow.value?.eventLogs;

  if (!eventLogs || !Array.isArray(eventLogs)) {
    return null;
  }
  
  let searchSteps = stepId === WorkflowStatus.OrderProcessing ? [stepId, WorkflowStatus.OrderUpdated] : [stepId];
  
  if (isTyreOrder.value) {
    const currentStepOrder = getCurrentStepOrder();
    const stepConfig = activeProgressSteps.value.find(s => s.id === stepId);
    
    if (stepConfig && stepConfig.order >= currentStepOrder) {
      return null;
    }
    
    const tyreStepMapping: Record<string, string[]> = {
      [TyreOrderStep.SupplierApproval]: [WorkflowStatus.OrderApproval],
      [TyreOrderStep.SupplierAcceptation]: [WorkflowStatus.OrderApproval],
      [TyreOrderStep.DeliveryConfirmation]: [WorkflowStatus.OrderProcessing, WorkflowStatus.OrderUpdated],
      [TyreOrderStep.Delivered]: [WorkflowStatus.Completed]
    };
    
    searchSteps = tyreStepMapping[stepId] || [stepId];
    
    const matchingEvent = eventLogs.find(event => searchSteps.includes(event.workflowStatus) && event.description?.includes(stepId));
    
    return matchingEvent || null;
  }

  const filteredEvents = eventLogs.filter(event => searchSteps.includes(event.workflowStatus));

  if (filteredEvents.length === 0) {
    return null;
  }
  
  const newestEvent = filteredEvents.sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())[0];

  return newestEvent;
}

const getCurrentStepOrder = () => {
  if (!workflow.value) return 0;
  
  const mappedStatus = mapStatusForDisplay(workflow.value.status);
  const currentStep = activeProgressSteps.value.find(step => step.id === mappedStatus);
  
  return currentStep?.order || 0;
};

const getProgressFillClass = (currentStepId: string, nextStepId: string) => {
  const currentOrder = getCurrentStepOrder();
  const nextStepOrder = activeProgressSteps.value.find(s => s.id === nextStepId)?.order || 0;

  if (currentOrder >= nextStepOrder) {
    if (isCurrentStep(nextStepId) && !isLastStep(nextStepId)) {
      return '';
    }
    
    return 'bg-green-500';
  }
  
  return 'bg-transparent';
};

const getProgressFillStyle = (currentStepId: string, nextStepId: string) => {
  const currentOrder = getCurrentStepOrder();
  const nextStepOrder = activeProgressSteps.value.find(s => s.id === nextStepId)?.order || 0;

  if (currentOrder >= nextStepOrder) {
    if (isCurrentStep(nextStepId) && !isLastStep(nextStepId)) {
      return 'width: 100%; background: linear-gradient(to right, #22c55e 0%, #22c55e 30%, #3b82f6 70%, #3b82f6 100%)';
    }
    return 'width: 100%';
  }
  
  return 'width: 0%';
};

const getStepCircleClass = (stepId: string) => {
  const currentOrder = getCurrentStepOrder();
  const stepOrder = activeProgressSteps.value.find(s => s.id === stepId)?.order || 0;
  
  if (isCurrentStep(stepId) && isLastStep(stepId)) {
    return 'bg-green-500 border-2 border-green-500';
  } else if (isCurrentStep(stepId)) {
    return 'bg-blue-500 border-2 border-blue-500 shadow-lg';
  } else if (currentOrder > stepOrder) {
    return 'bg-green-500 border-2 border-green-500';
  } else {
    return 'bg-gray-300 border-2 border-gray-300';
  }
};

const getStepTextClass = (stepId: string) => {
  const currentOrder = getCurrentStepOrder();
  const stepOrder = activeProgressSteps.value.find(s => s.id === stepId)?.order || 0;
  
  if (isCurrentStep(stepId) && isLastStep(stepId)) {
    return 'text-green-600 dark:text-green-400 font-semibold';
  } else if (isCurrentStep(stepId)) {
    return 'text-blue-600 dark:text-blue-400 font-semibold';
  } else if (currentOrder > stepOrder) {
    return 'text-green-600 dark:text-green-400 font-semibold';
  } else {
    return 'text-gray-400 dark:text-gray-400 font-semibold';
  }
};

const isCurrentStep = (stepId: string) => {
  const mappedStatus = mapStatusForDisplay(workflow.value?.status);
  return mappedStatus === stepId;
};

const mapStatusForDisplay = (status: string): string => {
  if (status === WorkflowStatus.OrderUpdated) {
    return WorkflowStatus.OrderProcessing;
  }
  
  if (isTyreOrder.value) {
    const currentStep = workflow.value?.purchaseOrders?.[props.purchaseOrderIndex]?.currentProcessStep;
    
    if (status === WorkflowStatus.OrderRejection || status === WorkflowStatus.Rejected || status === WorkflowStatus.Archived) {
      return status;
    }

    if (status === WorkflowStatus.Completed || currentStep === TyreOrderStep.Delivered) {
      return TyreOrderStep.Delivered;
    }
    
    if (currentStep === TyreOrderStep.Rejection || currentStep === TyreOrderStep.Rejected) {
      return WorkflowStatus.OrderRejection;
    }
    
    if (currentStep === TyreOrderStep.Parked) {
      return TyreOrderStep.SupplierApproval;
    }
    
    if (currentStep) {
      return currentStep;
    }
  }
  
  return status;
};

const isLastStep = (stepId: string) => {
  const maxOrder = Math.max(...filteredSteps.value.map(step => step.order));
  const currentStep = activeProgressSteps.value.find(step => step.id === stepId);
  
  return currentStep ? currentStep.order === maxOrder : false;
};

const getCurrentProcessStep = (): string | null => {
  if (!workflow.value?.status) return null;
  
  switch (workflowPhase.value) {
    case 'quotation':
      return workflow.value.quotation?.currentProcessStep ?? null;
      
    case 'order':
      return workflow.value.purchaseOrders?.[props.purchaseOrderIndex]?.currentProcessStep ?? null;
      
    case 'delivery':
      if (isTyreOrder.value) {
        return workflow.value.purchaseOrders?.[props.purchaseOrderIndex]?.currentProcessStep ?? null;
      }
      
      return workflow.value.delivery?.currentProcessStep ?? null;
      
    default:
      return null;
  }
};

const currentProcessStepLabel = computed(() => {
  const step = getCurrentProcessStep();
  if (!step) return null;
  
  const status = workflow.value?.status;
  if (!status) return null;
  
  const statusKey = status.charAt(0).toLowerCase() + status.slice(1);
  const stepKey = step.charAt(0).toLowerCase() + step.slice(1);
  
  const translationKey = `processStatus.step.${statusKey}.${stepKey}`;
  const translated = t(translationKey);
  
  if (translated === translationKey) {
    const purchaseOrder = workflow.value?.purchaseOrders?.[props.purchaseOrderIndex];
    
    if (purchaseOrder?.type === PurchaseOrder.Tyre && statusKey !== 'orderApproval') {
      const fallbackKey = `processStatus.step.orderApproval.${stepKey}`;
      const fallbackTranslated = t(fallbackKey);
      
      if (fallbackTranslated !== fallbackKey) {
        return fallbackTranslated;
      }
    }
    
    return step.replace(/([A-Z])/g, ' $1').trim();
  }
  
  return translated;
});

const isSubmitQuotationAdjustmentDisabled = computed(() => {
  if (actionLoading.value) return true;
  if (!isDriverFormValid.value) return true;
  if (!meaningRequiredDeliveryDate.value) return true;

  const modesNeedDateCheck = ['LE', 'GE', 'EQ'];
  const mode = meaningRequiredDeliveryDate.value;
  
  if (modesNeedDateCheck.includes(mode)) {
    const dateObj = requiredDeliveryDate.value;
    if (!dateObj) return true;

    const { year, month, day } = dateObj;
    const selectedDate = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selectedDate <= today) return true;
  }
  
  return false;
});

async function confirmDeliveryRejection() {
  actionLoading.value = true;
  
  try {
    const completeStep = await apiAdapter.invokeRaw<WorkflowDto>(
      requestConfig => api.workflowDeliveryCompletestep(workflow.value?.externalReference, requestConfig),
      (error) => showValidationErrors(error, t)
    );
    
    if (completeStep && completeStep.type === 'success') {
      const { data, response } = completeStep;
      
      emit('reload', data, response.headers['etag']);
      showSuccess(t('notification.deliveryRejectedSuccessfully'));
    }
  } catch (error) {
    showError(t('notification.rejectFailed'));
  } finally {
    actionLoading.value = false;
  }
}

async function approveStep(stepId: string) {
  actionLoading.value = true;
  
  try {
    if (stepId === QuotationStep.SchemaFinalization || stepId === QuotationStep.DriverApproval || stepId === QuotationStep.HRApproval || stepId === QuotationStep.ClientApproval || stepId === QuotationStep.ClientAccountAcceptation || stepId === QuotationStep.OrderCreation || stepId === QuotationStep.FleetManagerApproval || stepId == QuotationStep.PurchaseSupportApproval || stepId === QuotationStep.SupervisorApproval || stepId === QuotationStep.GroupApproval || stepId === QuotationStep.PersonApproval || (stepId === QuotationStep.Rejection && !isTyreOrder.value)) {
      if (stepId === QuotationStep.ClientAccountAcceptation) {
        const patch = jsonpatch.compare(
          {
            quotation: {
              initialQuotationNumber: workflow.value?.quotation?.initialQuotationNumber
            }
          },
          {
            quotation: {
              initialQuotationNumber: initialQuotationNumber.value
            }
          }
        )
        
        if (patch.length > 0) {
          await apiAdapter.invoke<WorkflowDto>(
              requestConfig => api.patchWorkflow(workflow.value?.externalReference, patch, requestConfig),
              (error) => showValidationErrors(error, t)
          );
        }
      } else if (stepId === QuotationStep.DriverApproval && isSsnFlow.value) {
        const ssnResult = await apiAdapter.invokeRaw<void>(
            requestConfig => api.ssn(workflow.value?.externalReference, { ssn: socialSecurityNumber.value.trim().toUpperCase() } as SsnDto, requestConfig) as any,
            (error: any) => showValidationErrors(error, t)
        );
        
        if (!ssnResult || ssnResult.type !== 'success') {
          return;
        }
      }
      
      const completeStep = await apiAdapter.invokeRaw(
          requestConfig => api.workflowQuotationCompletestep(workflow.value?.externalReference, requestConfig),
          (error) => showValidationErrors(error, t)
      );
      
      if (completeStep && completeStep.type === 'success') {
        const { data, response } = completeStep;
        
        emit('reload', data, response.headers['etag']);
        showSuccess(t('notification.quotationApprovedSuccessfully'))
      }
    } else if (stepId === QuotationStep.NotRegisteredUserApproval) {
      const completeStep = await apiAdapter.invokeRaw(
        requestConfig => api.approve(props.slug!, { 
          ...requestConfig, 
          headers: { 
            ...requestConfig?.headers,
            'X-Slug-Token': props.slugToken || sessionStorage.getItem('slug-token-' + props.slug) || '' 
          }
        }),
        (error) => showValidationErrors(error, t)
      );
      
      if (completeStep && completeStep.type === 'success') {
        const { data, response } = completeStep;
        
        sessionStorage.removeItem('slug-token-' + props.slug)
        emit('reload', data, response.headers['etag']);
        showSuccess(t('notification.quotationApprovedSuccessfully'))
      }
    } else if (stepId === VehicleOrderStep.KamApproval || stepId === VehicleOrderStep.ODApproval || stepId === VehicleOrderStep.DealerAcceptation || stepId === VehicleOrderStep.DealerConfirmation || stepId === VehicleOrderStep.DealerRejectionConfirmation || stepId === VehicleOrderStep.DealerRejection || stepId === TyreOrderStep.SupplierApproval || stepId === TyreOrderStep.SupplierAcceptation || stepId === TyreOrderStep.Rejection) {
      if (stepId === VehicleOrderStep.DealerConfirmation) {
        const dealerExpectedDeliveryDateObj = dealerExpectedDeliveryDate.value;
        let dealerExpectedDeliveryDateStr: string | null = null;
        
        if (dealerExpectedDeliveryDateObj) {
          const { year, month, day } = dealerExpectedDeliveryDateObj;
          dealerExpectedDeliveryDateStr = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2,'0')}`;
        }
        
        const patch = jsonpatch.compare(
          {
            purchaseOrders: [{
              dealerExpectedDeliveryDate: workflow.value?.purchaseOrders?.[props.purchaseOrderIndex]?.dealerExpectedDeliveryDate
            }],
          },
          {
            purchaseOrders: [{
              dealerExpectedDeliveryDate: dealerExpectedDeliveryDateStr
            }],
          }
        ).map(operation => {
          if (operation.path.startsWith('/purchaseOrders/0/')) {
            return {
              ...operation,
              path: operation.path.replace('/purchaseOrders/0/', `/purchaseOrders/${props.purchaseOrderNumber}/`)
            };
          }
          return operation;
        });
        
        if (patch.length > 0) {
          await apiAdapter.invoke<WorkflowDto>(
              requestConfig => api.patchWorkflow(workflow.value?.externalReference, patch, requestConfig),
              (error) => showValidationErrors(error, t)
          );
        }
      }
      
      const completeStep = await apiAdapter.invokeRaw<WorkflowDto>(
        requestConfig => api.workflowPurchaseorderCompletestep(workflow.value?.externalReference, workflow.value?.purchaseOrders?.[props.purchaseOrderIndex]?.number, requestConfig),
        (error) => showValidationErrors(error, t)
      );
      
      if (completeStep && completeStep.type === 'success') {
        const { data, response } = completeStep;
        
        emit('reload', data, response.headers['etag']);
        showSuccess(t('notification.orderApprovedSuccessfully'))
      }
    } else if (stepId === DeliveryStep.HandoverPreparation) {
      const completeStep = await apiAdapter.invokeRaw<WorkflowDto>(
        requestConfig => api.workflowDeliveryCompletestep(workflow.value?.externalReference, requestConfig),
        (error) => showValidationErrors(error, t)
      );

      if (completeStep && completeStep.type === 'success') {
        const { data, response } = completeStep;
        
        emit('reload', data, response.headers['etag']);
        showSuccess(t('notification.deliveryApprovedSuccessfully'))
      }
    }
  } catch (error) {
    showError('Failed to approve: ' + error)
  } finally {
    actionLoading.value = false;
  }
}

async function rejectStep(stepId: string, reason?: string) {
  actionLoading.value = true;
  
  try {
    if (stepId === QuotationStep.SchemaFinalization || stepId === QuotationStep.DriverApproval || stepId === QuotationStep.HRApproval || stepId === QuotationStep.ClientApproval || stepId === QuotationStep.ClientAccountAcceptation || stepId === QuotationStep.OrderCreation || stepId === QuotationStep.FleetManagerApproval || stepId === QuotationStep.PurchaseSupportApproval || stepId === QuotationStep.SupervisorApproval || stepId === QuotationStep.GroupApproval || stepId === QuotationStep.PersonApproval) {
      const completeStep = await apiAdapter.invokeRaw<WorkflowDto>(
        requestConfig => api.workflowQuotationReject(workflow.value?.externalReference, reason?.trim() ? { reason: reason.trim() } : null, requestConfig),
        (error) => showValidationErrors(error, t)
      );
      
      if (completeStep && completeStep.type === 'success') {
        const { data, response } = completeStep;
        
        emit('reload', data, response.headers['etag']);
        showSuccess(t('notification.quotationRejectedSuccessfully'))
      }
    } else if (stepId === QuotationStep.NotRegisteredUserApproval) {
      const completeStep = await apiAdapter.invokeRaw<WorkflowDto>(
        requestConfig => api.reject(props.slug!, { reason: reason ?? '' }, {
          ...requestConfig,
          headers: { 
            ...requestConfig?.headers,
            'Content-Type': 'application/json',
            'X-Slug-Token': props.slugToken || sessionStorage.getItem('slug-token-' + props.slug) || '' 
          }
        }),
        (error) => showValidationErrors(error, t)
      );

      if (completeStep && completeStep.type === 'success') {
        const { data, response } = completeStep;

        sessionStorage.removeItem('slug-token-' + props.slug)
        emit('reload', data, response.headers['etag']);
        showSuccess(t('notification.quotationRejectedSuccessfully'))
      }
    } else if (stepId === VehicleOrderStep.KamApproval || stepId === VehicleOrderStep.ODApproval || stepId === VehicleOrderStep.DealerAcceptation || stepId === VehicleOrderStep.DealerConfirmation || stepId === TyreOrderStep.SupplierApproval || stepId === TyreOrderStep.SupplierAcceptation) {
      const initiator = [Permission.VehicleSupplier, Permission.SupplierSuperUser, Permission.TyreSupplier, Permission.Dealer, Permission.PurchaseSupport].some(p => userStore.hasPermission(p)) ? Initiator.Supplier : Initiator.Client;
      
      const completeStep = await apiAdapter.invokeRaw<WorkflowDto>(
        requestConfig => api.workflowPurchaseorderReject(workflow.value?.externalReference, workflow.value?.purchaseOrders?.[props.purchaseOrderIndex]?.number, { initiator, reason: reason ?? '' }, requestConfig),
        (error) => showValidationErrors(error, t)
      );
      
      if (completeStep && completeStep.type === 'success') {
        const { data, response } = completeStep;
        
        emit('reload', data, response.headers['etag']);
        showSuccess(t('notification.orderRejectedSuccessfully'))
      }
    }
  } catch (error) {
    showError('Failed to reject: ' + error)
  } finally {
    actionLoading.value = false;
  }
}

async function resendNotRegisteredUserApprovalLink() {
  notRegisteredUserResendLoading.value = true
  
  try {
    const result = await apiAdapter.invokeRaw<ResendResultDto>(
      requestConfig => api.workflowSlugsResend(workflow.value?.externalReference!, requestConfig),
      (error) => showValidationErrors(error, t)
    );
    
    if (result && result.type === 'success') {
      if (result.data.count > 0) {
        showSuccess(t('notification.notRegisteredUserApprovalLinkResent', {
          emails: result.data.emails.join(', ')
        }));
      } else {
        showWarning(t('notification.notRegisteredUserApprovalLinkNoExpired'));
      }
    }
  } catch (error) {
    showError(t('notification.notRegisteredUserApprovalLinkResendFailed'));
  } finally {
    notRegisteredUserResendLoading.value = false;
  }
}

async function unparkWorkflow(scenario: UnparkScenario) {
  actionLoading.value = true;
  
  try {
    const result = await apiAdapter.invokeRaw<void>(
      requestConfig => api.workflowParkingDecision(workflow.value?.externalReference!, { scenario }, requestConfig) as any,
      (error: any) => showValidationErrors(error, t)
    );

    if (result && result.type === 'success') {
      const refreshed = await apiAdapter.invokeRaw<WorkflowDto>(
        requestConfig => api.getWorkflow(workflow.value?.externalReference!, undefined, undefined, requestConfig)
      );
      
      if (refreshed && refreshed.type === 'success') {
        emit('reload', refreshed.data, refreshed.response.headers['etag']);
      } else {
        emit('reload');
      }
      
      showSuccess(t('notification.workflowUnparkedSuccessfully'));
    }
  } catch (error) {
    showError('Failed to unpark: ' + error);
  } finally {
    actionLoading.value = false;
  }
}

async function unparkOrder(scenario: UnparkOrderScenario) {
  actionLoading.value = true;
  
  try {
    const poNumber = workflow.value?.purchaseOrders?.[props.purchaseOrderIndex]?.number;
    
    const result = await apiAdapter.invokeRaw<void>(
      requestConfig => api.orderParkingDecision(workflow.value?.externalReference!, poNumber!, { scenario }, requestConfig) as any,
      (error: any) => showValidationErrors(error, t)
    );
    
    if (result && result.type === 'success') {
      const refreshed = await apiAdapter.invokeRaw<WorkflowDto>(
        requestConfig => api.getWorkflow(workflow.value?.externalReference!, undefined, undefined, requestConfig)
      );
      
      if (refreshed && refreshed.type === 'success') {
        emit('reload', refreshed.data, refreshed.response.headers['etag']);
      } else {
        emit('reload');
      }
      
      showSuccess(t('notification.workflowUnparkedSuccessfully'));
    }
  } catch (error) {
    showError('Failed to unpark order: ' + error);
  } finally {
    actionLoading.value = false;
  }
}

const submitQuotationAdjustments = async (stepId: string) => {
  actionLoading.value = true
  
  try {
    const patch = jsonpatch.compare(
      {
        driverId: workflow.value?.driverId,
      },
      {
        driverId: selectedDriverOption.value ?? null,
      }
    );

    if (meaningRequiredDeliveryDate !== null) {
      const currentMode = workflow.value?.quotation?.requestedDeliveryDateScenario?.mode ?? null;
      const currentDate = workflow.value?.quotation?.requestedDeliveryDateScenario?.date ?? null;
      const requestedMode = meaningRequiredDeliveryDate.value;
      const requestedDateObj = requiredDeliveryDate.value;
      
      let requestedDateStr: string | null = null;
      if (requestedDateObj) {
        const { year, month, day } = requestedDateObj;
        const localDate = new Date(year, month - 1, day);
        requestedDateStr = localDate.toISOString().replace('.000Z', 'Z');
      }
      let needPatch = false;
      let newDelivery: any = null;
      
      switch (requestedMode) {
        case 'LE':
        case 'GE':
        case 'EQ':
          if (currentMode !== requestedMode || currentDate !== requestedDateStr) {
            needPatch = true;
            newDelivery = {
              date: requestedDateStr,
              mode: requestedMode,
            };
          }
          break;
        case 'ASAP':
          if (currentMode !== 'ASAP') {
            needPatch = true;
            newDelivery = {
              mode: 'ASAP',
            };
          }
          break;
        default:
          break;
      }
      
      if (needPatch && newDelivery) {
        patch.push({
          op: "replace",
          path: "/quotation/requestedDeliveryDateScenario",
          value: newDelivery,
        });
      }
    }
    
    const existingPlate = workflow.value?.replacedCar?.licensePlate ?? null;
    const inputPlate = replacedPlate.value === "" ? null : replacedPlate.value;
    
    if (existingPlate !== inputPlate) {
      patch.push({
        op: "replace",
        path: "/replacedCar",
        value: {
          licensePlate: inputPlate
        },
      });
    }
    
    if (wantsNewDriver.value) {
      patch.push({
        op: "replace",
        path: "/driverContact",
        value: {
          firstName: driverFirstName.value.trim(),
          surname: driverLastName.value.trim(),
          phoneNumber: driverPhone.value.trim(),
          email: driverEmail.value.trim(),
        },
      });
    }
    
    let patchResult: ApiAdapterResult<WorkflowDto> | undefined;
    
    if (patch.length > 0) {
      patchResult = await apiAdapter.invokeRaw<WorkflowDto>(
        requestConfig => api.patchWorkflow(workflow.value?.externalReference, patch, requestConfig), 
        (error) => showValidationErrors(error, t)
      );
    }

    if (stepId === QuotationStep.ClientAccountAcceptation) {
      emit('reload',
        patchResult?.type === 'success' ? patchResult.data : undefined,
        patchResult?.type === 'success' ? patchResult.response.headers['etag'] : undefined
      );
      showSuccess(t('notification.quotationInformationSaveSuccessfully'))
    } else {
      const completeStep = await apiAdapter.invokeRaw<WorkflowDto>(
        requestConfig => api.workflowQuotationCompletestep(workflow.value?.externalReference, requestConfig), 
        (error) => showValidationErrors(error, t)
      );

      if (completeStep && completeStep.type === 'success') {
        const { data, response } = completeStep;
        
        emit('reload', data, response.headers['etag']);
        showSuccess(t(stepId === QuotationStep.ClientApproval
          ? 'notification.quotationApprovedSuccessfully'
          : 'notification.quotationInformationSaveSuccessfully'))
      }
    }
  } catch (err) {
    showError(t('notification.quotationInformationSaveFailed'))
  } finally {
    actionLoading.value = false
  }
}

const submitDeliveryInfo = async (stepId: string) => {
  actionLoading.value = true

  try {
    let patch: any[] = []

    if (stepId === DeliveryStep.Waiting) {
      if (workflow.value?.tenant === Tenant.FI) {
        const dealerExpectedDeliveryDateObj = dealerExpectedDeliveryDate.value;
        let dealerExpectedDeliveryDateStr: string | null = null;
        
        if (dealerExpectedDeliveryDateObj) {
          const { year, month, day } = dealerExpectedDeliveryDateObj;
          dealerExpectedDeliveryDateStr = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2,'0')}`;
        }
        
        patch = jsonpatch.compare(
          {
            delivery: {
              vin: workflow.value?.delivery?.vin,
              licensePlate: workflow.value?.delivery?.licensePlate
            },
            purchaseOrders: [{
              dealerExpectedDeliveryDate: workflow.value?.purchaseOrders?.[props.purchaseOrderIndex]?.dealerExpectedDeliveryDate
            }],
          },
          {
            delivery: {
              vin: vinNumber.value,
              licensePlate: licensePlate.value
            },
            purchaseOrders: [{
              dealerExpectedDeliveryDate: dealerExpectedDeliveryDateStr
            }],
          }
        ).map(operation => {
          if (operation.path.startsWith('/purchaseOrders/0/')) {
            return {
              ...operation,
              path: operation.path.replace('/purchaseOrders/0/', `/purchaseOrders/${props.purchaseOrderNumber ?? ''}/`)
            };
          }
          return operation;
        });
      } else {
        patch = jsonpatch.compare(
          {
            delivery: {
              vin: workflow.value?.delivery?.vin,
              co2: workflow.value?.delivery?.cO2
            }
          },
          {
            delivery: {
              vin: vinNumber.value,
              co2: co2Emissions.value === '' ? null : parseInt(co2Emissions.value, 10)
            }
          }
        )
      }
    } else if (stepId === DeliveryStep.VehicleRegistration) {
      const registrationDateObj = registrationDate.value;
      
      let registrationDateStr: string | null = null;
      if (registrationDateObj) {
        const { year, month, day } = registrationDateObj;
        registrationDateStr = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2,'0')}`;
      }
      
      patch = jsonpatch.compare(
        {
          delivery: {
            licensePlate: workflow.value?.delivery?.licensePlate,
            registrationDate: workflow.value?.delivery?.registrationDate
          }
        },
        {
          delivery: {
            licensePlate: licensePlate.value,
            registrationDate: registrationDateStr
          }
        }
      )
    } else if (stepId === DeliveryStep.Handover) {
      const handoverDateObj = handoverDate.value;

      let handoverDateStr: string | null = null;
      if (handoverDateObj) {
        const { year, month, day } = handoverDateObj;
        handoverDateStr = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2,'0')}`;
      }

      patch = jsonpatch.compare(
        {
          delivery: {
            handoverDate: workflow.value?.delivery?.handoverDate,
            handoverMileage: workflow.value?.delivery?.handoverMileage
          }
        },
        {
          delivery: {
            handoverDate: handoverDateStr,
            handoverMileage: handoverMileage.value
          }
        }
      )
    } else if (stepId === TyreOrderStep.DeliveryConfirmation) {
      const newValue = secondTyreSetDeliveryInfo.value.trim() === '' ? null : secondTyreSetDeliveryInfo.value.trim();
      
      let secondTyreSetDeliveryDateStr: string | null = null;
      if (secondTyreSetDeliveryDate.value) {
        const { year, month, day } = secondTyreSetDeliveryDate.value;
        secondTyreSetDeliveryDateStr = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2,'0')}`;
      }
      
      patch = jsonpatch.compare(
        {
          purchaseOrders: [{
            tyreLocationAddress: workflow.value?.purchaseOrders?.[props.purchaseOrderIndex]?.tyreLocationAddress ?? null,
            deliveryDate: workflow.value?.purchaseOrders?.[props.purchaseOrderIndex]?.deliveryDate ?? null
          }]
        },
        {
          purchaseOrders: [{
            tyreLocationAddress: newValue,
            deliveryDate: secondTyreSetDeliveryDateStr
          }]
        }
      ).map(operation => {
        if (operation.path.startsWith('/purchaseOrders/0/')) {
          return {
            ...operation,
            path: operation.path.replace('/purchaseOrders/0/', `/purchaseOrders/${props.purchaseOrderNumber}/`)
          };
        }
        return operation;
      });
    }
    
    let patchResult: ApiAdapterResult<WorkflowDto> | undefined;
    
    if (patch.length > 0) {
      patchResult = await apiAdapter.invokeRaw<WorkflowDto>(
        requestConfig => api.patchWorkflow(workflow.value?.externalReference, patch, requestConfig),
        (error) => showValidationErrors(error, t)
      );
    }
    
    if (stepId === DeliveryStep.Waiting) {
      emit('reload',
        patchResult?.type === 'success' ? patchResult.data : undefined,
        patchResult?.type === 'success' ? patchResult.response.headers['etag'] : undefined
      );
      
      showSuccess(t('notification.deliveryInformationSaveSuccessfully'))
    } else if (stepId === TyreOrderStep.DeliveryConfirmation) {
      const completeStep = await apiAdapter.invokeRaw<WorkflowDto>(
        requestConfig => api.workflowPurchaseorderCompletestep(workflow.value?.externalReference, workflow.value?.purchaseOrders?.[props.purchaseOrderIndex]?.number, requestConfig),
        (error) => showValidationErrors(error, t)
      );
      
      if (completeStep && completeStep.type === 'success') {
        const { data, response } = completeStep;
        
        emit('reload', data, response.headers['etag']);
        showSuccess(t('notification.tyreDeliveryConfirmedSuccessfully'))
      }
    } else {
      const completeStep = await apiAdapter.invokeRaw<WorkflowDto>(
        requestConfig => api.workflowDeliveryCompletestep(workflow.value?.externalReference, requestConfig),
        (error) => showValidationErrors(error, t)
      );
      
      if (completeStep && completeStep.type === 'success') {
        const { data, response } = completeStep;
        
        emit('reload', data, response.headers['etag']);
        
        if (stepId === DeliveryStep.Handover) {
          showSuccess(t('notification.handoverInformationSaveSuccessfully'))
        } else {
          showSuccess(t('notification.deliveryInformationSaveSuccessfully'))
        }
      }
    }
  } catch (err) {
    if (stepId === DeliveryStep.Handover) {
      showError(t('notification.handoverInformationSaveFailed'))
    } else {
      showError(t('notification.deliveryInformationSaveFailed'))
    }
  } finally {
    actionLoading.value = false
  }
}

const formatDeliveryDate = (dateObject) => {
  if (!dateObject) return null

  const year = dateObject.year
  const month = String(dateObject.month).padStart(2, '0')
  const day = String(dateObject.day).padStart(2, '0')
  
  //return `${year}-${month}-${day}T00:00:00.000Z`
  return `${year}-${month}-${day}`
}

watch(wantsNewDriver, (newValue) => {
  if (!newValue) {
    driverFirstName.value = existingDriverContact?.firstName ?? ''
    driverLastName.value = existingDriverContact?.surname ?? ''
    driverPhone.value = existingDriverContact?.phoneNumber ?? ''
    driverEmail.value = existingDriverContact?.email ?? ''
  }
})

watch(() => props.driver, (driver) => {
  if (driver?.$type === 'DriverSsnDto') {
    socialSecurityNumber.value = driver.ssn ?? ''
  }
})

watch(replacedPlate, (newValue, oldValue) => {
  const hasValueNow = newValue && newValue.trim() !== '';
  const hadValueBefore = oldValue && oldValue.trim() !== '';
  
  if (hasValueNow && !hadValueBefore) {
    if (requiredDeliveryDate.value) {
      const { year, month, day } = requiredDeliveryDate.value;
      const jsDate = new Date(year, month - 1, day);
      const dec15 = new Date(year, 11, 15);
      const dec31 = new Date(year, 11, 31);
      
      if (jsDate >= dec15 && jsDate <= dec31) {
        requiredDeliveryDate.value = null;
      }
    }
  }
});
</script>